import { lazy, Suspense } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../components/AuthProvider';
import { useSubscription } from '../hooks/useSubscription';
import WatermarkRemoverClient from '../components/WatermarkRemover.client';
import { useLocaleContext } from '../context/LocaleContext';
import { ChromeBadge } from '../components/ChromeBadge';
import { SocialProof } from '../components/SocialProof';
import { RelatedContentForPSEO } from '../components/RelatedContentForPSEO';
import GeminiFAQAccordion from '../components/GeminiFAQAccordion';

// CRITICAL FIX: Import these directly instead of lazy loading.
// These components are already in the main bundle (imported directly in App.tsx for HomePage).
// Lazy loading them here created separate chunks that weren't loaded during hydration,
// causing Suspense fallback to show → hydration mismatch → re-render → CLS.
import DocumentCleaners from '../components/DocumentCleaners';
import Comparison from '../components/Comparison';
import FeedbackCTA from '../components/FeedbackCTA';
import GeminiImageBanner from '../components/GeminiImageBanner';

// Lazy load components that are unique to pSEO pages (not in main bundle)
const RelatedTools = lazy(() => import('../components/RelatedTools'));
const RelatedArticles = lazy(() => import('../components/RelatedArticles'));
import {
  Sparkles,
  CheckCircle,
  Shield,
  Zap,
  ArrowRight,
  MessageCircle,
  Mail,
  TrendingUp,
  Globe,
  BarChart3,
  PenTool,
  Briefcase,
  Image,
} from 'lucide-react';

export default function GeminiWatermarkPage() {
  const { user } = useAuth();
  const { hasActiveSubscription } = useSubscription();
  const { locale } = useLocaleContext();

  return (
    <div className="min-h-screen bg-white dark:bg-neutral-950">
      {/* Custom Hero for Gemini */}
      {/* CRITICAL FIX: Added min-h-* to prevent CLS from decorative blur elements.
          The blur elements use `top-1/2 -translate-y-1/2` (centered vertically).
          If section height changes during hydration, their position shifts → CLS.
          min-height ensures section height is stable even during hydration. */}
      <section className="relative overflow-hidden py-16 md:py-20 lg:py-24 min-h-[600px] md:min-h-[650px] lg:min-h-[700px]">
        {/* Background decorative elements */}
        {/* CLS FIX: Changed from percentage-based positioning (top-1/2) to fixed positioning (top-[200px]).
            Percentage positions cause elements to shift when content height changes.
            Fixed positions remain stable regardless of content height. */}
        <div className="absolute inset-0 -z-10 overflow-hidden">
          <div className="absolute left-1/2 top-0 -translate-x-1/2 blur-3xl opacity-20 w-[40rem] h-[40rem]">
            <div className="h-full w-full rounded-full bg-blue-100 dark:bg-blue-900/20"></div>
          </div>
          <div className="absolute right-0 top-[200px] blur-3xl opacity-20 w-[30rem] h-[30rem]">
            <div className="h-full w-full rounded-full bg-slate-100 dark:bg-slate-800/20"></div>
          </div>
        </div>

        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left Column - Content */}
            <div className="space-y-8 text-center lg:text-left">
              {/* Badge */}
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 dark:bg-blue-900/20 rounded-full text-blue-700 dark:text-blue-300 text-sm font-medium border border-blue-100 dark:border-blue-800">
                <Sparkles size={16} />
                <span>
                  {locale === 'de'
                    ? 'Google Gemini Wasserzeichen-Entfernung'
                    : locale === 'pt-BR'
                    ? 'Remoção de Marca d\'Água do Google Gemini'
                    : locale === 'ms-MY'
                    ? 'Penyingkiran Tanda Air Google Gemini'
                    : locale === 'es'
                    ? 'Eliminación de Marcas de Agua de Google Gemini'
                    : locale === 'fr'
                    ? 'Suppression du Filigrane Google Gemini'
                    : locale === 'ja'
                    ? 'Google Gemini 透かし削除'
                    : locale === 'id'
                    ? 'Penghapusan Watermark Google Gemini'
                    : 'Google Gemini Watermark Removal'}
                </span>
              </div>

              {/* Main Heading - H1 with primary keyword */}
              <h1 className="font-bold text-neutral-900 dark:text-white leading-tight">
                <span className="block text-4xl md:text-5xl lg:text-6xl text-blue-600 dark:text-blue-400">
                  {locale === 'de'
                    ? 'Google Gemini Wasserzeichen entfernen'
                    : locale === 'pt-BR'
                    ? 'Remover Marcas d\'Água do Google Gemini'
                    : locale === 'ms-MY'
                    ? 'Buang Tanda Air Google Gemini'
                    : locale === 'es'
                    ? 'Eliminar Marcas de Agua de Google Gemini'
                    : locale === 'fr'
                    ? 'Supprimer les Filigranes Google Gemini'
                    : locale === 'ja'
                    ? 'Google Gemini 透かしを削除'
                    : locale === 'id'
                    ? 'Hapus Watermark Google Gemini'
                    : 'Remove Google Gemini Watermarks'}
                </span>
                <span className="block text-2xl md:text-3xl lg:text-4xl mt-2 text-slate-700 dark:text-slate-300">
                  {locale === 'de'
                    ? 'Erkennen und Entfernen Sie unsichtbare KI-Wasserzeichen'
                    : locale === 'pt-BR'
                    ? 'Detecte e Remova Marcas d\'Água Invisíveis de IA'
                    : locale === 'ms-MY'
                    ? 'Kesan dan Buang Tanda Air AI yang Tidak Kelihatan'
                    : locale === 'es'
                    ? 'Detecte y Elimine Marcas de Agua Invisibles de IA'
                    : locale === 'fr'
                    ? 'Détectez et Supprimez les Filigranes IA Invisibles'
                    : locale === 'ja'
                    ? '見えないAI透かしを検出して削除'
                    : locale === 'id'
                    ? 'Deteksi dan Hapus Watermark AI yang Tak Terlihat'
                    : 'Detect and Remove Invisible AI Watermarks'}
                </span>
              </h1>

              {/* Description with keywords */}
              <p className="text-lg md:text-xl text-neutral-600 dark:text-neutral-400 leading-relaxed max-w-2xl mx-auto lg:mx-0">
                {locale === 'de'
                  ? 'Entfernen Sie unsichtbare Unicode-Zeichen aus Text, den Sie aus Google Gemini (ehemals Bard) kopiert haben. Unser Tool erkennt und entfernt Zeichen ohne Breite und andere unsichtbare Formatierungsartefakte aus jeder Gemini-Ausgabe. Es hat keinen Einfluss auf SynthID, Googles tatsächliches KI-Wasserzeichen, das in der Wortwahl eingebettet ist und nicht durch das Entfernen von Zeichen beseitigt werden kann.'
                  : locale === 'pt-BR'
                  ? 'Remova caracteres Unicode invisíveis de texto copiado do Google Gemini (anteriormente Bard). Nossa ferramenta detecta e remove caracteres de largura zero e outros artefatos de formatação invisíveis de qualquer saída do Gemini. Isso não afeta o SynthID, a marca d\'água de IA real do Google, que está incorporada na escolha de palavras e não pode ser removida ao eliminar caracteres.'
                  : locale === 'ms-MY'
                  ? 'Buang aksara Unicode yang tidak kelihatan daripada teks yang anda tampal daripada Google Gemini (dahulunya Bard). Alat kami mengesan dan membuang aksara lebar-sifar dan artifak pemformatan tidak kelihatan lain daripada sebarang output Gemini. Ia tidak menjejaskan SynthID, tanda air AI sebenar Google, yang terbenam dalam pilihan perkataan dan tidak boleh dibuang dengan membuang aksara.'
                  : locale === 'es'
                  ? 'Elimina caracteres Unicode invisibles del texto que copiaste de Google Gemini (anteriormente Bard). Nuestra herramienta detecta y elimina caracteres de ancho cero y otros artefactos de formato invisibles de cualquier salida de Gemini. No afecta a SynthID, la marca de agua de IA real de Google, que está incrustada en la elección de palabras y no se puede eliminar borrando caracteres.'
                  : locale === 'fr'
                  ? 'Supprimez les caractères Unicode invisibles du texte que vous avez copié depuis Google Gemini (anciennement Bard). Notre outil détecte et supprime les caractères de largeur nulle et autres artefacts de formatage invisibles de toute sortie Gemini. Cela n\'affecte pas SynthID, le véritable filigrane IA de Google, qui est intégré dans le choix des mots et ne peut pas être supprimé en effaçant des caractères.'
                  : locale === 'ja'
                  ? 'Google Gemini（旧Bard）からコピーしたテキストから不可視のUnicode文字を削除します。当社のツールは、Geminiの出力に含まれるゼロ幅文字やその他の不可視のフォーマットアーティファクトを検出して削除します。これは、GoogleのAI透かしであるSynthIDには影響しません。SynthIDは単語の選択に埋め込まれており、文字を削除しても取り除くことはできません。'
                  : locale === 'id'
                  ? 'Hapus karakter Unicode tak terlihat dari teks yang Anda salin dari Google Gemini (sebelumnya Bard). Alat kami mendeteksi dan menghapus karakter lebar nol dan artefak format tak terlihat lainnya dari output Gemini apa pun. Ini tidak mempengaruhi SynthID, watermark AI asli Google, yang tertanam dalam pilihan kata dan tidak dapat dihapus dengan menghapus karakter.'
                  : 'Remove invisible Unicode characters from text you\'ve pasted out of Google Gemini (formerly Bard). Our tool detects and strips zero-width characters and other invisible formatting artifacts from any Gemini output. It does not affect SynthID, Google\'s actual AI watermark, which is embedded in word choice and cannot be removed by stripping characters.'}
              </p>

              {/* Cross-link to Image Watermark Removal */}
              <Link
                to={locale === 'de' ? '/de/gemini-bild-wasserzeichen-entfernen'
                  : locale === 'pt-BR' ? '/pt-br/remover-marca-dagua-imagem-gemini'
                  : locale === 'ms-MY' ? '/my/buang-tanda-air-imej-gemini'
                  : locale === 'es' ? '/es/eliminar-marca-agua-imagen-gemini'
                  : locale === 'fr' ? '/fr/supprimer-filigrane-image-gemini'
                  : locale === 'ja' ? '/ja/gemini-gazou-watermark-sakujo'
                  : locale === 'id' ? '/id/hapus-watermark-gambar-gemini'
                  : '/remove-gemini-image-watermark'}
                className="inline-flex items-center gap-2 px-4 py-2 bg-purple-50 dark:bg-purple-900/20 rounded-lg text-purple-700 dark:text-purple-300 text-sm font-medium border border-purple-200 dark:border-purple-800 hover:bg-purple-100 dark:hover:bg-purple-900/30 transition-colors"
              >
                <Image size={16} className="flex-shrink-0" />
                <span>
                  {locale === 'de'
                    ? 'Suchen Sie nach Bild-Wasserzeichen-Entfernung?'
                    : locale === 'pt-BR'
                    ? 'Procurando remoção de marca d\'água de imagem?'
                    : locale === 'ms-MY'
                    ? 'Mencari penyingkiran tanda air imej?'
                    : locale === 'es'
                    ? '¿Busca eliminar marcas de agua de imagen?'
                    : locale === 'fr'
                    ? 'Vous cherchez à supprimer les filigranes d\'image ?'
                    : locale === 'ja'
                    ? '画像の透かし削除をお探しですか？'
                    : locale === 'id'
                    ? 'Mencari penghapusan watermark gambar?'
                    : 'Looking for image watermark removal?'}
                </span>
                <ArrowRight size={16} />
              </Link>
              <p className="text-xs text-neutral-500 dark:text-neutral-500 max-w-md mx-auto lg:mx-0">
                {locale === 'de'
                  ? 'Das Bild-Tool entfernt nur das sichtbare Gemini-Wasserzeichen; SynthID in Bildpixeln bleibt bestehen.'
                  : locale === 'pt-BR'
                  ? 'A ferramenta de imagem remove apenas a marca d\'água visível do Gemini; o SynthID nos pixels da imagem permanece.'
                  : locale === 'ms-MY'
                  ? 'Alat imej hanya membuang tanda air Gemini yang kelihatan; SynthID dalam piksel imej kekal.'
                  : locale === 'es'
                  ? 'La herramienta de imágenes solo elimina la marca de agua visible de Gemini; el SynthID en los píxeles de la imagen permanece.'
                  : locale === 'fr'
                  ? 'L\'outil image supprime uniquement le filigrane visible de Gemini ; le SynthID dans les pixels de l\'image subsiste.'
                  : locale === 'ja'
                  ? '画像ツールは見えるGeminiの透かしのみを削除します。画像ピクセル内のSynthIDは残ります。'
                  : locale === 'id'
                  ? 'Alat gambar hanya menghapus watermark Gemini yang terlihat; SynthID pada piksel gambar tetap ada.'
                  : 'The image tool removes only the visible Gemini watermark; SynthID in the image pixels remains.'}
              </p>

              {/* Feature List with Gemini-specific benefits */}
              <div className="space-y-3 text-left max-w-md mx-auto lg:mx-0">
                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                    <CheckCircle size={16} className="text-blue-600 dark:text-blue-400" />
                  </div>
                  <span className="text-neutral-700 dark:text-neutral-300">
                    {locale === 'de'
                      ? 'Funktioniert mit Text aus jedem Gemini-Modell'
                      : locale === 'pt-BR'
                      ? 'Funciona com texto de qualquer modelo Gemini'
                      : locale === 'ms-MY'
                      ? 'Berfungsi dengan teks daripada mana-mana model Gemini'
                      : locale === 'es'
                      ? 'Funciona con texto de cualquier modelo Gemini'
                      : locale === 'fr'
                      ? 'Fonctionne avec du texte de n\'importe quel modèle Gemini'
                      : locale === 'ja'
                      ? 'あらゆるGeminiモデルのテキストに対応'
                      : locale === 'id'
                      ? 'Bekerja dengan teks dari model Gemini apa pun'
                      : 'Works with text from any Gemini model'}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                    <CheckCircle size={16} className="text-blue-600 dark:text-blue-400" />
                  </div>
                  <span className="text-neutral-700 dark:text-neutral-300">
                    {locale === 'de'
                      ? 'Entfernt unsichtbare Zeichen aus eingefügtem Text'
                      : locale === 'pt-BR'
                      ? 'Remove caracteres invisíveis do texto colado'
                      : locale === 'ms-MY'
                      ? 'Membuang aksara tidak kelihatan daripada teks yang ditampal'
                      : locale === 'es'
                      ? 'Elimina caracteres invisibles del texto pegado'
                      : locale === 'fr'
                      ? 'Supprime les caractères invisibles du texte collé'
                      : locale === 'ja'
                      ? '貼り付けたテキストから見えない文字を削除'
                      : locale === 'id'
                      ? 'Menghapus karakter tak terlihat dari teks yang ditempel'
                      : 'Removes invisible characters from pasted text'}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                    <CheckCircle size={16} className="text-blue-600 dark:text-blue-400" />
                  </div>
                  <span className="text-neutral-700 dark:text-neutral-300">
                    {locale === 'de'
                      ? '100% privat - keine Daten gespeichert'
                      : locale === 'pt-BR'
                      ? '100% privado - nenhum dado armazenado'
                      : locale === 'ms-MY'
                      ? '100% peribadi - tiada data disimpan'
                      : locale === 'es'
                      ? '100% privado - no se almacenan datos'
                      : locale === 'fr'
                      ? '100% privé - aucune donnée stockée'
                      : locale === 'ja'
                      ? '100%プライベート - データ保存なし'
                      : locale === 'id'
                      ? '100% privat - tidak ada data tersimpan'
                      : '100% private - no data stored'}
                  </span>
                </div>
              </div>

              {/* CTA Button - CLS FIX: min-h and specific transitions like main Hero */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start min-h-[56px]">
                <a
                  href="#remover"
                  className="group inline-flex items-center justify-center gap-2 px-8 py-4 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold text-lg shadow-sm hover:shadow transition-[background-color,box-shadow] duration-200"
                >
                  <Zap size={20} />
                  <span>
                    {locale === 'de'
                      ? 'Jetzt unsichtbare Zeichen entfernen'
                      : locale === 'pt-BR'
                      ? 'Remover Caracteres Invisíveis Agora'
                      : locale === 'ms-MY'
                      ? 'Buang Aksara Tidak Kelihatan Sekarang'
                      : locale === 'es'
                      ? 'Eliminar Caracteres Invisibles Ahora'
                      : locale === 'fr'
                      ? 'Supprimer les Caractères Invisibles Maintenant'
                      : locale === 'ja'
                      ? '今すぐ見えない文字を削除'
                      : locale === 'id'
                      ? 'Hapus Karakter Tak Terlihat Sekarang'
                      : 'Remove Invisible Characters Now'}
                  </span>
                  <ArrowRight
                    size={20}
                    className="group-hover:translate-x-1 transition-transform"
                  />
                </a>
              </div>

              {/* Social Proof - CLS FIX: SocialProof has internal min-h */}
              <div className="flex justify-center lg:justify-start pt-6 min-h-[100px]">
                <SocialProof />
              </div>

              {/* Chrome Extension Badge - CLS FIX: min-h for consistent height */}
              <div className="flex justify-center lg:justify-start pt-4 min-h-[44px]">
                <ChromeBadge />
              </div>
            </div>

            {/* Right Column - Visual - Hidden on mobile (matches main Hero to prevent CLS) */}
            <div className="hidden lg:block relative px-8 lg:px-8 lg:pr-16">
              {/* CLS FIX: Using exact same classes as main Hero visual card */}
              <div className="relative bg-white dark:bg-neutral-900 rounded-2xl shadow-xl border border-neutral-200 dark:border-neutral-800 p-8">
                <div className="relative space-y-6">
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-blue-600 rounded-xl text-white">
                      <img src="/favicon.svg" alt="Logo" width="24" height="24" className="w-6 h-6" />
                    </div>
                    <div>
                      <div className="text-sm text-neutral-500 dark:text-neutral-400">
                        {locale === 'de'
                          ? 'Unsichtbare Zeichen'
                          : locale === 'pt-BR'
                          ? 'Caracteres Invisíveis'
                          : locale === 'ms-MY'
                          ? 'Aksara Tidak Kelihatan'
                          : locale === 'es'
                          ? 'Caracteres Invisibles'
                          : locale === 'fr'
                          ? 'Caractères Invisibles'
                          : locale === 'ja'
                          ? '見えない文字'
                          : locale === 'id'
                          ? 'Karakter Tak Terlihat'
                          : 'Invisible Characters'}
                      </div>
                      <div className="text-2xl font-bold text-neutral-900 dark:text-white">
                        {locale === 'de'
                          ? 'Beispiel'
                          : locale === 'pt-BR'
                          ? 'Exemplo'
                          : locale === 'ms-MY'
                          ? 'Contoh'
                          : locale === 'es'
                          ? 'Ejemplo'
                          : locale === 'fr'
                          ? 'Exemple'
                          : locale === 'ja'
                          ? '例'
                          : locale === 'id'
                          ? 'Contoh'
                          : 'Example'}
                      </div>
                    </div>
                  </div>

                  <div className="p-4 bg-neutral-50 dark:bg-charcoal-800 rounded-xl border-2 border-dashed border-neutral-300 dark:border-charcoal-700">
                    <div className="text-sm text-neutral-600 dark:text-neutral-400 font-mono leading-relaxed">
                      {locale === 'de'
                        ? 'Text, der aus einem'
                        : locale === 'pt-BR'
                        ? 'Texto colado de uma'
                        : locale === 'ms-MY'
                        ? 'Teks yang ditampal daripada'
                        : locale === 'es'
                        ? 'El texto pegado de una'
                        : locale === 'fr'
                        ? 'Le texte collé depuis un'
                        : locale === 'ja'
                        ? 'AIツールから貼り付けた'
                        : locale === 'id'
                        ? 'Teks yang ditempel dari'
                        : 'Text pasted from an'}
                      <span className="bg-yellow-200 dark:bg-yellow-900/50 px-1 mx-0.5 rounded">​</span>
                      {locale === 'de'
                        ? 'KI-Tool eingefügt wird, kann'
                        : locale === 'pt-BR'
                        ? 'ferramenta de IA pode conter'
                        : locale === 'ms-MY'
                        ? 'alat AI boleh mengandungi'
                        : locale === 'es'
                        ? 'herramienta de IA puede contener'
                        : locale === 'fr'
                        ? 'outil IA peut contenir'
                        : locale === 'ja'
                        ? 'テキストには'
                        : locale === 'id'
                        ? 'alat AI dapat berisi'
                        : 'AI tool can contain'}
                      <span className="bg-yellow-200 dark:bg-yellow-900/50 px-1 mx-0.5 rounded">​</span>
                      {locale === 'de'
                        ? 'versteckte Zeichen enthalten'
                        : locale === 'pt-BR'
                        ? 'caracteres ocultos'
                        : locale === 'ms-MY'
                        ? 'aksara tersembunyi'
                        : locale === 'es'
                        ? 'caracteres ocultos'
                        : locale === 'fr'
                        ? 'des caractères cachés'
                        : locale === 'ja'
                        ? '隠し文字が含まれることがあります'
                        : locale === 'id'
                        ? 'karakter tersembunyi'
                        : 'hidden characters'}
                    </div>
                  </div>

                  <div className="flex justify-center">
                    {/* CLS FIX: Removed animate-bounce - CSS animations can trigger CLS measurements */}
                    <div className="p-2 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full text-white">
                      <ArrowRight size={20} className="rotate-90" />
                    </div>
                  </div>

                  <div className="p-4 bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-xl border-2 border-green-300 dark:border-green-800">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle size={16} className="text-green-600 dark:text-green-400" />
                      <span className="text-xs font-semibold text-green-700 dark:text-green-300">
                        {locale === 'de'
                          ? 'Bereinigt'
                          : locale === 'pt-BR'
                          ? 'Limpo'
                          : locale === 'ms-MY'
                          ? 'Dibersihkan'
                          : locale === 'es'
                          ? 'Limpiado'
                          : locale === 'fr'
                          ? 'Nettoyé'
                          : locale === 'ja'
                          ? 'クリーン済み'
                          : locale === 'id'
                          ? 'Dibersihkan'
                          : 'Cleaned'}
                      </span>
                    </div>
                    <div className="text-sm text-neutral-700 dark:text-neutral-300 font-mono leading-relaxed">
                      {locale === 'de'
                        ? 'Text, der aus einem KI-Tool eingefügt wird, kann versteckte Zeichen enthalten'
                        : locale === 'pt-BR'
                        ? 'Texto colado de uma ferramenta de IA pode conter caracteres ocultos'
                        : locale === 'ms-MY'
                        ? 'Teks yang ditampal daripada alat AI boleh mengandungi aksara tersembunyi'
                        : locale === 'es'
                        ? 'El texto pegado de una herramienta de IA puede contener caracteres ocultos'
                        : locale === 'fr'
                        ? 'Le texte collé depuis un outil IA peut contenir des caractères cachés'
                        : locale === 'ja'
                        ? 'AIツールから貼り付けたテキストには隠し文字が含まれることがあります'
                        : locale === 'id'
                        ? 'Teks yang ditempel dari alat AI dapat berisi karakter tersembunyi'
                        : 'Text pasted from an AI tool can contain hidden characters'}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-4 pb-12">
        {/* Main Watermark Remover Tool */}
        <WatermarkRemoverClient user={user} />

        {/* Document Cleaners - directly imported (no longer lazy) */}
        <DocumentCleaners user={user} />

        {/* Educational Content Section - Rich in keywords */}
        <section className="mt-16">
          <div className="bg-gradient-to-br from-white to-blue-50 dark:from-gray-800 dark:to-blue-900/20 rounded-3xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden p-8 md:p-12">
            <div className="flex items-center gap-4 mb-8">
              <div className="p-3 bg-gradient-to-r from-blue-500 to-purple-600 rounded-2xl text-white">
                <Sparkles className="w-8 h-8" />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-gray-800 dark:text-white">
                  {locale === 'de'
                    ? 'Wie Google Gemini Wasserzeichen funktionieren'
                    : locale === 'pt-BR'
                    ? 'Como Funcionam as Marcas d\'Água do Google Gemini'
                    : locale === 'ms-MY'
                    ? 'Bagaimana Tanda Air Google Gemini Berfungsi'
                    : locale === 'es'
                    ? 'Cómo Funcionan las Marcas de Agua de Google Gemini'
                    : locale === 'fr'
                    ? 'Comment Fonctionnent les Filigranes Google Gemini'
                    : locale === 'ja'
                    ? 'Google Gemini透かしの仕組み'
                    : locale === 'id'
                    ? 'Cara Kerja Watermark Google Gemini'
                    : 'How Google Gemini Watermarks Work'}
                </h2>
                <p className="text-gray-600 dark:text-gray-400 mt-1">
                  {locale === 'de'
                    ? 'Verstehen Sie die Technologie hinter Gemini KI-Wasserzeichen'
                    : locale === 'pt-BR'
                    ? 'Entenda a tecnologia por trás das marcas d\'água de IA do Gemini'
                    : locale === 'ms-MY'
                    ? 'Memahami teknologi di sebalik tanda air AI Gemini'
                    : locale === 'es'
                    ? 'Comprendiendo la tecnología detrás de las marcas de agua de IA de Gemini'
                    : locale === 'fr'
                    ? 'Comprendre la technologie derrière les filigranes IA de Gemini'
                    : locale === 'ja'
                    ? 'Gemini AI透かしの背後にある技術を理解する'
                    : locale === 'id'
                    ? 'Memahami teknologi di balik watermark AI Gemini'
                    : 'Understanding the technology behind Gemini AI watermarks'}
                </p>
              </div>
            </div>

            <div className="prose dark:prose-invert max-w-none">
              {locale === 'de' ? (
                <>
                  <h3 className="text-xl font-semibold text-gray-800 dark:text-white mb-4">
                    Warum erscheinen unsichtbare Zeichen in Gemini-Text?
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300 mb-6 leading-relaxed">
                    Text, den Sie aus Gemini kopieren, enthält manchmal unsichtbare Unicode-Zeichen
                    wie Zeichen ohne Breite und weiche Bindestriche. Die wahrscheinlichste Erklärung
                    ist ein Formatierungsartefakt, das beim Rendern und Kopieren des Textes entsteht.
                    Das ist etwas anderes als SynthID, Googles tatsächliches KI-Wasserzeichen, das bei der
                    Generierung in Geminis Wortwahl eingebettet wird und in der Token-Sequenz selbst
                    steckt, nicht in einzelnen Unicode-Zeichen. Kein Zeichen-Entfernungstool, auch
                    nicht dieses, kann SynthID erkennen oder entfernen.
                  </p>

                  <h3 className="text-xl font-semibold text-gray-800 dark:text-white mb-4">
                    Welche unsichtbaren Zeichen dieses Tool entfernt
                  </h3>
                  <ul className="space-y-3 mb-6">
                    <li className="flex items-start gap-3">
                      <CheckCircle
                        size={20}
                        className="text-blue-600 dark:text-blue-400 flex-shrink-0 mt-1"
                      />
                      <span className="text-gray-700 dark:text-gray-300">
                        <strong>Leerzeichen ohne Breite (U+200B)</strong> - Vollständig unsichtbare
                        Leerzeichen zwischen Wörtern
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle
                        size={20}
                        className="text-blue-600 dark:text-blue-400 flex-shrink-0 mt-1"
                      />
                      <span className="text-gray-700 dark:text-gray-300">
                        <strong>Nicht-Verbinder ohne Breite (U+200C)</strong> - Verhindert
                        Zeichenkombination
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle
                        size={20}
                        className="text-blue-600 dark:text-blue-400 flex-shrink-0 mt-1"
                      />
                      <span className="text-gray-700 dark:text-gray-300">
                        <strong>Verbinder ohne Breite (U+200D)</strong> - Erzwingt
                        Zeichenkombination
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle
                        size={20}
                        className="text-blue-600 dark:text-blue-400 flex-shrink-0 mt-1"
                      />
                      <span className="text-gray-700 dark:text-gray-300">
                        <strong>Weicher Bindestrich (U+00AD)</strong> - Bedingte Zeilenumbruchpunkte
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle
                        size={20}
                        className="text-blue-600 dark:text-blue-400 flex-shrink-0 mt-1"
                      />
                      <span className="text-gray-700 dark:text-gray-300">
                        <strong>Wortverbinder (U+2060)</strong> - Verhindert Zeilenumbrüche
                      </span>
                    </li>
                  </ul>

                  <h3 className="text-xl font-semibold text-gray-800 dark:text-white mb-4">
                    Wann sollten Sie Gemini-Text bereinigen?
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                    Es gibt legitime Gründe, unsichtbare Zeichen aus Gemini-Text zu entfernen:
                    Behebung von Formatierungsproblemen beim Kopieren in andere Anwendungen, Entfernung
                    von Artefakten, die Textverarbeitungstools stören, und die Wahrung der
                    Kopier-Einfüge-Treue, damit das, was Sie teilen, dem entspricht, was Sie sehen -
                    sowie die Bereinigung von Text für den persönlichen Gebrauch, wo diese versprengten
                    Zeichen nicht benötigt werden. Unser Tool bietet eine einfache Möglichkeit, diese
                    Zeichen zu entfernen und dabei Ihren Inhalt zu erhalten.
                  </p>
                </>
              ) : locale === 'pt-BR' ? (
                <>
                  <h3 className="text-xl font-semibold text-gray-800 dark:text-white mb-4">
                    Por que caracteres invisíveis aparecem em texto do Gemini?
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300 mb-6 leading-relaxed">
                    O texto copiado do Gemini às vezes contém caracteres Unicode invisíveis, como
                    espaços de largura zero e hífens suaves. A explicação mais provável é um artefato
                    de formatação decorrente de como o texto é renderizado e copiado. Isso é diferente
                    do SynthID, a
                    marca d'água de IA real do Google, que é incorporada nas escolhas de palavras do
                    Gemini no momento da geração e reside na própria sequência de tokens, não em
                    caracteres Unicode isolados. Nenhuma ferramenta de remoção de caracteres, incluindo
                    esta, consegue detectar ou remover o SynthID.
                  </p>

                  <h3 className="text-xl font-semibold text-gray-800 dark:text-white mb-4">
                    Quais Caracteres Invisíveis Esta Ferramenta Remove
                  </h3>
                  <ul className="space-y-3 mb-6">
                    <li className="flex items-start gap-3">
                      <CheckCircle
                        size={20}
                        className="text-blue-600 dark:text-blue-400 flex-shrink-0 mt-1"
                      />
                      <span className="text-gray-700 dark:text-gray-300">
                        <strong>Espaço de Largura Zero (U+200B)</strong> - Espaços completamente invisíveis
                        inseridos entre palavras
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle
                        size={20}
                        className="text-blue-600 dark:text-blue-400 flex-shrink-0 mt-1"
                      />
                      <span className="text-gray-700 dark:text-gray-300">
                        <strong>Não-Juntor de Largura Zero (U+200C)</strong> - Impede combinação de
                        caracteres
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle
                        size={20}
                        className="text-blue-600 dark:text-blue-400 flex-shrink-0 mt-1"
                      />
                      <span className="text-gray-700 dark:text-gray-300">
                        <strong>Juntor de Largura Zero (U+200D)</strong> - Força combinação de caracteres
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle
                        size={20}
                        className="text-blue-600 dark:text-blue-400 flex-shrink-0 mt-1"
                      />
                      <span className="text-gray-700 dark:text-gray-300">
                        <strong>Hífen Suave (U+00AD)</strong> - Pontos de quebra de linha condicionais
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle
                        size={20}
                        className="text-blue-600 dark:text-blue-400 flex-shrink-0 mt-1"
                      />
                      <span className="text-gray-700 dark:text-gray-300">
                        <strong>Juntor de Palavra (U+2060)</strong> - Impede quebras de linha entre
                        caracteres
                      </span>
                    </li>
                  </ul>

                  <h3 className="text-xl font-semibold text-gray-800 dark:text-white mb-4">
                    Quando Você Deve Limpar o Texto do Gemini?
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                    Existem razões legítimas para limpar caracteres invisíveis do texto do Gemini:
                    corrigir problemas de formatação ao copiar para outros aplicativos, remover artefatos
                    que interferem com ferramentas de processamento de texto e preservar a fidelidade de
                    copiar e colar, para que o que você compartilha seja o que você vê - além de limpar
                    texto para uso pessoal, onde esses caracteres perdidos não são necessários. Nossa
                    ferramenta oferece uma maneira simples de remover esses caracteres preservando seu
                    conteúdo.
                  </p>
                </>
              ) : locale === 'ms-MY' ? (
                <>
                  <h3 className="text-xl font-semibold text-gray-800 dark:text-white mb-4">
                    Mengapa Aksara Tidak Kelihatan Muncul dalam Teks Gemini?
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300 mb-6 leading-relaxed">
                    Teks yang disalin daripada Gemini kadangkala mengandungi aksara Unicode yang tidak
                    kelihatan, seperti ruang lebar-sifar dan tanda sempang lembut. Penjelasan yang
                    paling mungkin ialah artifak pemformatan daripada cara teks dipaparkan dan disalin.
                    Ini berbeza daripada SynthID,
                    tanda air AI sebenar Google, yang dibenamkan dalam pilihan perkataan Gemini semasa
                    penjanaan dan wujud dalam jujukan token itu sendiri, bukan dalam aksara Unicode yang
                    terpisah-pisah. Tiada alat pembuangan aksara, termasuk alat ini, boleh mengesan atau
                    membuang SynthID.
                  </p>

                  <h3 className="text-xl font-semibold text-gray-800 dark:text-white mb-4">
                    Aksara Tidak Kelihatan yang Dibuang oleh Alat Ini
                  </h3>
                  <ul className="space-y-3 mb-6">
                    <li className="flex items-start gap-3">
                      <CheckCircle
                        size={20}
                        className="text-blue-600 dark:text-blue-400 flex-shrink-0 mt-1"
                      />
                      <span className="text-gray-700 dark:text-gray-300">
                        <strong>Ruang Lebar-Sifar (U+200B)</strong> - Ruang yang sepenuhnya tidak kelihatan
                        disisipkan di antara perkataan
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle
                        size={20}
                        className="text-blue-600 dark:text-blue-400 flex-shrink-0 mt-1"
                      />
                      <span className="text-gray-700 dark:text-gray-300">
                        <strong>Bukan-Penyambung Lebar-Sifar (U+200C)</strong> - Menghalang gabungan
                        aksara
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle
                        size={20}
                        className="text-blue-600 dark:text-blue-400 flex-shrink-0 mt-1"
                      />
                      <span className="text-gray-700 dark:text-gray-300">
                        <strong>Penyambung Lebar-Sifar (U+200D)</strong> - Memaksa gabungan aksara
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle
                        size={20}
                        className="text-blue-600 dark:text-blue-400 flex-shrink-0 mt-1"
                      />
                      <span className="text-gray-700 dark:text-gray-300">
                        <strong>Tanda Sempang Lembut (U+00AD)</strong> - Peluang pemisah baris bersyarat
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle
                        size={20}
                        className="text-blue-600 dark:text-blue-400 flex-shrink-0 mt-1"
                      />
                      <span className="text-gray-700 dark:text-gray-300">
                        <strong>Penyambung Perkataan (U+2060)</strong> - Menghalang pemisah baris antara
                        aksara
                      </span>
                    </li>
                  </ul>

                  <h3 className="text-xl font-semibold text-gray-800 dark:text-white mb-4">
                    Bilakah Anda Perlu Membersihkan Teks Gemini?
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                    Terdapat sebab yang sah untuk membersihkan aksara tidak kelihatan daripada teks Gemini:
                    membetulkan isu pemformatan semasa menyalin ke aplikasi lain, membuang artifak yang
                    mengganggu alat pemprosesan teks, dan mengekalkan ketepatan salin-tampal supaya apa
                    yang anda kongsikan adalah apa yang anda lihat - selain membersihkan teks untuk
                    kegunaan peribadi di mana aksara terpencil ini tidak diperlukan. Alat kami menyediakan
                    cara mudah untuk membuang aksara ini sambil mengekalkan kandungan anda.
                  </p>
                </>
              ) : locale === 'es' ? (
                <>
                  <h3 className="text-xl font-semibold text-gray-800 dark:text-white mb-4">
                    ¿Por Qué Aparecen Caracteres Invisibles en el Texto de Gemini?
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300 mb-6 leading-relaxed">
                    El texto copiado de Gemini a veces contiene caracteres Unicode invisibles, como
                    espacios de ancho cero y guiones suaves. La explicación más probable es un
                    artefacto de formato derivado de cómo se renderiza y copia el texto. Esto es
                    distinto de SynthID, la marca
                    de agua de IA real de Google, que se incrusta en las elecciones de palabras de Gemini
                    en el momento de la generación y reside en la propia secuencia de tokens, no en
                    caracteres Unicode sueltos. Ninguna herramienta de eliminación de caracteres, incluida
                    esta, puede detectar ni eliminar SynthID.
                  </p>

                  <h3 className="text-xl font-semibold text-gray-800 dark:text-white mb-4">
                    Qué Caracteres Invisibles Elimina Esta Herramienta
                  </h3>
                  <ul className="space-y-3 mb-6">
                    <li className="flex items-start gap-3">
                      <CheckCircle
                        size={20}
                        className="text-blue-600 dark:text-blue-400 flex-shrink-0 mt-1"
                      />
                      <span className="text-gray-700 dark:text-gray-300">
                        <strong>Espacio de Ancho Cero (U+200B)</strong> - Espacios completamente invisibles
                        insertados entre palabras
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle
                        size={20}
                        className="text-blue-600 dark:text-blue-400 flex-shrink-0 mt-1"
                      />
                      <span className="text-gray-700 dark:text-gray-300">
                        <strong>No Conector de Ancho Cero (U+200C)</strong> - Evita la combinación de
                        caracteres
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle
                        size={20}
                        className="text-blue-600 dark:text-blue-400 flex-shrink-0 mt-1"
                      />
                      <span className="text-gray-700 dark:text-gray-300">
                        <strong>Conector de Ancho Cero (U+200D)</strong> - Fuerza la combinación de caracteres
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle
                        size={20}
                        className="text-blue-600 dark:text-blue-400 flex-shrink-0 mt-1"
                      />
                      <span className="text-gray-700 dark:text-gray-300">
                        <strong>Guión Suave (U+00AD)</strong> - Oportunidades de salto de línea condicionales
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle
                        size={20}
                        className="text-blue-600 dark:text-blue-400 flex-shrink-0 mt-1"
                      />
                      <span className="text-gray-700 dark:text-gray-300">
                        <strong>Conector de Palabras (U+2060)</strong> - Evita saltos de línea entre
                        caracteres
                      </span>
                    </li>
                  </ul>

                  <h3 className="text-xl font-semibold text-gray-800 dark:text-white mb-4">
                    ¿Cuándo Debe Limpiar el Texto de Gemini?
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                    Existen razones legítimas para limpiar caracteres invisibles del texto de Gemini:
                    corregir problemas de formato al copiar a otras aplicaciones, eliminar artefactos
                    que interfieren con las herramientas de procesamiento de texto y preservar la
                    fidelidad de copiar y pegar, para que lo que compartas sea lo que ves, además de
                    limpiar texto para uso personal donde estos caracteres sueltos no son necesarios.
                    Nuestra herramienta ofrece una forma sencilla de eliminar estos caracteres
                    conservando tu contenido.
                  </p>
                </>
              ) : locale === 'fr' ? (
                <>
                  <h3 className="text-xl font-semibold text-gray-800 dark:text-white mb-4">
                    Pourquoi des Caractères Invisibles Apparaissent-ils dans le Texte Gemini ?
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300 mb-6 leading-relaxed">
                    Le texte copié depuis Gemini contient parfois des caractères Unicode invisibles,
                    comme des espaces de largeur nulle et des traits d'union conditionnels.
                    L'explication la plus probable est un artefact de formatage lié à la façon dont
                    le texte est affiché puis copié. C'est distinct de SynthID, le véritable filigrane
                    IA de Google, qui est intégré dans les
                    choix de mots de Gemini au moment de la génération et réside dans la séquence de
                    tokens elle-même, pas dans des caractères Unicode isolés. Aucun outil de suppression
                    de caractères, y compris celui-ci, ne peut détecter ni supprimer SynthID.
                  </p>

                  <h3 className="text-xl font-semibold text-gray-800 dark:text-white mb-4">
                    Quels Caractères Invisibles Cet Outil Supprime
                  </h3>
                  <ul className="space-y-3 mb-6">
                    <li className="flex items-start gap-3">
                      <CheckCircle
                        size={20}
                        className="text-blue-600 dark:text-blue-400 flex-shrink-0 mt-1"
                      />
                      <span className="text-gray-700 dark:text-gray-300">
                        <strong>Espace de Largeur Nulle (U+200B)</strong> - Espaces complètement invisibles
                        insérés entre les mots
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle
                        size={20}
                        className="text-blue-600 dark:text-blue-400 flex-shrink-0 mt-1"
                      />
                      <span className="text-gray-700 dark:text-gray-300">
                        <strong>Non-Jointeur de Largeur Nulle (U+200C)</strong> - Empêche la combinaison
                        de caractères
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle
                        size={20}
                        className="text-blue-600 dark:text-blue-400 flex-shrink-0 mt-1"
                      />
                      <span className="text-gray-700 dark:text-gray-300">
                        <strong>Jointeur de Largeur Nulle (U+200D)</strong> - Force la combinaison de caractères
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle
                        size={20}
                        className="text-blue-600 dark:text-blue-400 flex-shrink-0 mt-1"
                      />
                      <span className="text-gray-700 dark:text-gray-300">
                        <strong>Trait d'Union Conditionnel (U+00AD)</strong> - Points de césure conditionnels
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle
                        size={20}
                        className="text-blue-600 dark:text-blue-400 flex-shrink-0 mt-1"
                      />
                      <span className="text-gray-700 dark:text-gray-300">
                        <strong>Jointeur de Mots (U+2060)</strong> - Empêche les sauts de ligne entre caractères
                      </span>
                    </li>
                  </ul>

                  <h3 className="text-xl font-semibold text-gray-800 dark:text-white mb-4">
                    Quand Devez-vous Nettoyer le Texte Gemini ?
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                    Il existe des raisons légitimes de nettoyer les caractères invisibles du texte Gemini :
                    corriger les problèmes de formatage lors de la copie vers d'autres applications, supprimer
                    les artefacts qui interfèrent avec les outils de traitement de texte, et préserver la
                    fidélité du copier-coller pour que ce que vous partagez soit ce que vous voyez, en plus
                    de nettoyer le texte pour un usage personnel où ces caractères égarés ne sont pas
                    nécessaires. Notre outil offre un moyen simple de supprimer ces caractères tout en
                    préservant votre contenu.
                  </p>
                </>
              ) : locale === 'ja' ? (
                <>
                  <h3 className="text-xl font-semibold text-gray-800 dark:text-white mb-4">
                    なぜGeminiのテキストに見えない文字が現れるのか？
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300 mb-6 leading-relaxed">
                    Geminiからコピーしたテキストには、ゼロ幅スペースやソフトハイフンなどの見えない
                    Unicode文字が含まれることがあります。テキストがレンダリングされコピーされる過程で
                    生じるフォーマットのアーティファクトである可能性が最も高いと考えられます。これは、
                    GoogleのAI透かしであるSynthIDとは別のものです。
                    SynthIDは生成時にGeminiの単語選択に埋め込まれ、個々のUnicode文字ではなくトークン列
                    そのものに存在します。このツールを含め、いかなる文字削除ツールもSynthIDを検出したり
                    削除したりすることはできません。
                  </p>

                  <h3 className="text-xl font-semibold text-gray-800 dark:text-white mb-4">
                    このツールが削除する見えない文字
                  </h3>
                  <ul className="space-y-3 mb-6">
                    <li className="flex items-start gap-3">
                      <CheckCircle
                        size={20}
                        className="text-blue-600 dark:text-blue-400 flex-shrink-0 mt-1"
                      />
                      <span className="text-gray-700 dark:text-gray-300">
                        <strong>ゼロ幅スペース (U+200B)</strong> - 単語間に挿入される完全に不可視のスペース
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle
                        size={20}
                        className="text-blue-600 dark:text-blue-400 flex-shrink-0 mt-1"
                      />
                      <span className="text-gray-700 dark:text-gray-300">
                        <strong>ゼロ幅非結合子 (U+200C)</strong> - 文字の結合を防止
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle
                        size={20}
                        className="text-blue-600 dark:text-blue-400 flex-shrink-0 mt-1"
                      />
                      <span className="text-gray-700 dark:text-gray-300">
                        <strong>ゼロ幅結合子 (U+200D)</strong> - 文字の結合を強制
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle
                        size={20}
                        className="text-blue-600 dark:text-blue-400 flex-shrink-0 mt-1"
                      />
                      <span className="text-gray-700 dark:text-gray-300">
                        <strong>ソフトハイフン (U+00AD)</strong> - 条件付き改行ポイント
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle
                        size={20}
                        className="text-blue-600 dark:text-blue-400 flex-shrink-0 mt-1"
                      />
                      <span className="text-gray-700 dark:text-gray-300">
                        <strong>単語結合子 (U+2060)</strong> - 文字間の改行を防止
                      </span>
                    </li>
                  </ul>

                  <h3 className="text-xl font-semibold text-gray-800 dark:text-white mb-4">
                    Geminiのテキストをいつクリーニングすべきか？
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                    Geminiのテキストから見えない文字をクリーニングする正当な理由があります。他の
                    アプリケーションにコピーする際のフォーマット問題の修正、テキスト処理ツールと干渉する
                    アーティファクトの除去、そして共有する内容が見た目どおりになるようコピー＆ペーストの
                    忠実性を保つこと、さらにこうした余分な文字が不要な個人利用のためのテキスト
                    クリーニングです。当社のツールは、コンテンツを保持しながらこれらの文字を削除する
                    簡単な方法を提供します。
                  </p>
                </>
              ) : locale === 'id' ? (
                <>
                  <h3 className="text-xl font-semibold text-gray-800 dark:text-white mb-4">
                    Mengapa Karakter Tak Terlihat Muncul di Teks Gemini?
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300 mb-6 leading-relaxed">
                    Teks yang disalin dari Gemini terkadang mengandung karakter Unicode tak terlihat,
                    seperti spasi lebar nol dan tanda hubung lunak. Penjelasan yang paling mungkin
                    adalah artefak format akibat cara teks dirender dan disalin. Ini berbeda dari
                    SynthID, watermark AI asli Google, yang
                    disematkan dalam pilihan kata Gemini saat pembuatan dan berada dalam urutan token itu
                    sendiri, bukan pada karakter Unicode yang terpisah. Tidak ada alat penghapus karakter,
                    termasuk alat ini, yang dapat mendeteksi atau menghapus SynthID.
                  </p>

                  <h3 className="text-xl font-semibold text-gray-800 dark:text-white mb-4">
                    Karakter Tak Terlihat yang Dihapus Alat Ini
                  </h3>
                  <ul className="space-y-3 mb-6">
                    <li className="flex items-start gap-3">
                      <CheckCircle
                        size={20}
                        className="text-blue-600 dark:text-blue-400 flex-shrink-0 mt-1"
                      />
                      <span className="text-gray-700 dark:text-gray-300">
                        <strong>Spasi Lebar Nol (U+200B)</strong> - Spasi yang sepenuhnya tak terlihat
                        disisipkan di antara kata
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle
                        size={20}
                        className="text-blue-600 dark:text-blue-400 flex-shrink-0 mt-1"
                      />
                      <span className="text-gray-700 dark:text-gray-300">
                        <strong>Non-Joiner Lebar Nol (U+200C)</strong> - Mencegah penggabungan karakter
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle
                        size={20}
                        className="text-blue-600 dark:text-blue-400 flex-shrink-0 mt-1"
                      />
                      <span className="text-gray-700 dark:text-gray-300">
                        <strong>Joiner Lebar Nol (U+200D)</strong> - Memaksa penggabungan karakter
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle
                        size={20}
                        className="text-blue-600 dark:text-blue-400 flex-shrink-0 mt-1"
                      />
                      <span className="text-gray-700 dark:text-gray-300">
                        <strong>Tanda Hubung Lunak (U+00AD)</strong> - Titik pemisah baris kondisional
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle
                        size={20}
                        className="text-blue-600 dark:text-blue-400 flex-shrink-0 mt-1"
                      />
                      <span className="text-gray-700 dark:text-gray-300">
                        <strong>Penggabung Kata (U+2060)</strong> - Mencegah pemisah baris antar karakter
                      </span>
                    </li>
                  </ul>

                  <h3 className="text-xl font-semibold text-gray-800 dark:text-white mb-4">
                    Kapan Anda Harus Membersihkan Teks Gemini?
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                    Ada alasan yang sah untuk membersihkan karakter tak terlihat dari teks Gemini:
                    memperbaiki masalah format saat menyalin ke aplikasi lain, menghapus artefak yang
                    mengganggu alat pemrosesan teks, dan menjaga kesetiaan salin-tempel agar apa yang
                    Anda bagikan sama dengan apa yang Anda lihat, selain membersihkan teks untuk
                    penggunaan pribadi di mana karakter-karakter liar ini tidak diperlukan. Alat kami
                    menyediakan cara sederhana untuk menghapus karakter ini sambil mempertahankan konten
                    Anda.
                  </p>
                </>
              ) : (
                <>
                  <h3 className="text-xl font-semibold text-gray-800 dark:text-white mb-4">
                    Why Do Invisible Characters Appear in Gemini Text?
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300 mb-6 leading-relaxed">
                    Text copied out of Gemini sometimes contains invisible Unicode characters, such as
                    zero-width spaces and soft hyphens. The most likely explanation is a formatting
                    artifact from how the text is rendered and copied. This is separate from SynthID,
                    Google's actual AI watermark,
                    which is embedded in Gemini's word choices at generation time and lives in the
                    token sequence itself, not in stray Unicode characters. No character-removal tool,
                    including this one, can detect or remove SynthID.
                  </p>

                  <h3 className="text-xl font-semibold text-gray-800 dark:text-white mb-4">
                    Which Invisible Characters This Tool Removes
                  </h3>
                  <ul className="space-y-3 mb-6">
                    <li className="flex items-start gap-3">
                      <CheckCircle
                        size={20}
                        className="text-blue-600 dark:text-blue-400 flex-shrink-0 mt-1"
                      />
                      <span className="text-gray-700 dark:text-gray-300">
                        <strong>Zero-Width Space (U+200B)</strong> - Completely invisible spaces
                        inserted between words
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle
                        size={20}
                        className="text-blue-600 dark:text-blue-400 flex-shrink-0 mt-1"
                      />
                      <span className="text-gray-700 dark:text-gray-300">
                        <strong>Zero-Width Non-Joiner (U+200C)</strong> - Prevents character
                        combination
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle
                        size={20}
                        className="text-blue-600 dark:text-blue-400 flex-shrink-0 mt-1"
                      />
                      <span className="text-gray-700 dark:text-gray-300">
                        <strong>Zero-Width Joiner (U+200D)</strong> - Forces character combination
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle
                        size={20}
                        className="text-blue-600 dark:text-blue-400 flex-shrink-0 mt-1"
                      />
                      <span className="text-gray-700 dark:text-gray-300">
                        <strong>Soft Hyphen (U+00AD)</strong> - Conditional line break opportunities
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle
                        size={20}
                        className="text-blue-600 dark:text-blue-400 flex-shrink-0 mt-1"
                      />
                      <span className="text-gray-700 dark:text-gray-300">
                        <strong>Word Joiner (U+2060)</strong> - Prevents line breaks between
                        characters
                      </span>
                    </li>
                  </ul>

                  <h3 className="text-xl font-semibold text-gray-800 dark:text-white mb-4">
                    When Should You Clean Gemini Text?
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                    There are legitimate reasons to clean invisible characters from Gemini text:
                    fixing formatting issues when copying to other applications, removing artifacts
                    that interfere with text processing tools, and preserving copy-paste fidelity so
                    what you share is what you see, as well as cleaning text for personal use where
                    these stray characters aren't needed. Our tool provides a simple way to remove
                    these characters while preserving your content.
                  </p>
                </>
              )}
            </div>
          </div>
        </section>

      {/* UNIQUE SECTION: Google Workspace Integration Use Cases */}
      <section className="mt-16">
        <div className="text-center mb-12">
          <div className="inline-block p-3 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-2xl text-white mb-4">
            <Globe className="w-8 h-8" />
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 dark:text-white mb-4">
            {locale === 'de'
              ? 'Wie professionelle Teams Gemini-Wasserzeichen entfernen'
              : locale === 'pt-BR'
              ? 'Como Equipes Profissionais Usam a Remoção de Marca d\'Água do Gemini'
              : locale === 'ms-MY'
              ? 'Bagaimana Pasukan Profesional Menggunakan Penyingkiran Tanda Air Gemini'
              : locale === 'es'
              ? 'Cómo los Equipos Profesionales Usan la Eliminación de Marcas de Agua de Gemini'
              : locale === 'fr'
              ? 'Comment les Équipes Professionnelles Utilisent la Suppression de Filigrane Gemini'
              : locale === 'ja'
              ? 'プロフェッショナルチームがGemini透かし削除を使用する方法'
              : locale === 'id'
              ? 'Cara Tim Profesional Menggunakan Penghapusan Watermark Gemini'
              : 'How Professional Teams Use Gemini Watermark Removal'}
          </h2>
          <p className="text-lg text-neutral-600 dark:text-neutral-400 max-w-3xl mx-auto">
            {locale === 'de'
              ? 'Google Gemini ist in Gmail, Docs, Sheets und andere Workspace-Tools integriert. Entdecken Sie, warum Fachleute unsichtbare Zeichen entfernen, bevor sie Gemini-generierte Inhalte teilen.'
              : locale === 'pt-BR'
              ? 'O Google Gemini se integra com Gmail, Docs, Sheets e outras ferramentas do Workspace. Descubra por que profissionais removem caracteres invisíveis antes de compartilhar conteúdo gerado pelo Gemini.'
              : locale === 'ms-MY'
              ? 'Google Gemini disepadukan dengan Gmail, Docs, Sheets, dan alat Workspace yang lain. Ketahui mengapa profesional membuang aksara tidak kelihatan sebelum berkongsi kandungan yang dijana Gemini.'
              : locale === 'es'
              ? 'Google Gemini se integra con Gmail, Docs, Sheets y otras herramientas de Workspace. Descubra por qué los profesionales eliminan caracteres invisibles antes de compartir contenido generado por Gemini.'
              : locale === 'fr'
              ? 'Google Gemini s\'intègre avec Gmail, Docs, Sheets et d\'autres outils Workspace. Découvrez pourquoi les professionnels suppriment les caractères invisibles avant de partager du contenu généré par Gemini.'
              : locale === 'ja'
              ? 'Google GeminiはGmail、Docs、Sheetsなどのワークスペースツールと統合されています。プロフェッショナルがGemini生成コンテンツを共有する前に見えない文字を削除する理由をご覧ください。'
              : locale === 'id'
              ? 'Google Gemini terintegrasi dengan Gmail, Docs, Sheets, dan alat Workspace lainnya. Temukan mengapa para profesional menghapus karakter tak terlihat sebelum membagikan konten yang dihasilkan Gemini.'
              : 'Google Gemini integrates with Gmail, Docs, Sheets, and other Workspace tools. Discover why professionals clean invisible characters before sharing Gemini-generated content.'}
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Use Case 1: Email Marketing with Gmail */}
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950/30 dark:to-indigo-950/30 p-8 rounded-2xl border-2 border-blue-200 dark:border-blue-800 hover:shadow-2xl transition-[box-shadow,transform] duration-300 hover:-translate-y-1">
            <div className="w-14 h-14 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-xl flex items-center justify-center mb-5 shadow-lg">
              <Mail className="text-white" size={28} />
            </div>
            <h3 className="text-xl font-bold text-neutral-900 dark:text-white mb-3">
              {locale === 'de'
                ? 'E-Mail-Marketing in Gmail'
                : locale === 'pt-BR'
                ? 'Marketing por E-mail no Gmail'
                : locale === 'ms-MY'
                ? 'Pemasaran E-mel dalam Gmail'
                : locale === 'es'
                ? 'Marketing por Correo Electrónico en Gmail'
                : locale === 'fr'
                ? 'Marketing par E-mail dans Gmail'
                : locale === 'ja'
                ? 'Gmailでのメールマーケティング'
                : locale === 'id'
                ? 'Pemasaran Email di Gmail'
                : 'Email Marketing in Gmail'}
            </h3>
            <p className="text-neutral-700 dark:text-neutral-300 mb-4 leading-relaxed">
              {locale === 'de'
                ? 'Marketingteams nutzen Gemini in Gmail, um Kampagnen-E-Mails, Newsletter und Kundenansprachen zu entwerfen. Unsichtbare Zeichen im kopierten Text können jedoch unprofessionell wirken, wenn sie in HTML-E-Mail-Vorlagen eingefügt werden. Das Entfernen dieser Zeichen vor der Finalisierung gewährleistet saubere, professionelle E-Mail-Kommunikation.'
                : locale === 'pt-BR'
                ? 'Equipes de marketing usam o Gemini no Gmail para redigir e-mails de campanha, newsletters e divulgação ao cliente. No entanto, caracteres invisíveis no texto copiado podem parecer pouco profissionais quando colados em modelos de e-mail HTML. Remover esses caracteres antes de finalizar garante comunicação por e-mail limpa e profissional.'
                : locale === 'ms-MY'
                ? 'Pasukan pemasaran menggunakan Gemini dalam Gmail untuk merangka e-mel kempen, surat berita, dan jangkauan pelanggan. Walau bagaimanapun, aksara tidak kelihatan dalam teks yang disalin boleh kelihatan tidak profesional apabila ditampal ke dalam templat e-mel HTML. Membuang aksara ini sebelum memuktamadkan memastikan komunikasi e-mel yang bersih dan profesional.'
                : locale === 'es'
                ? 'Los equipos de marketing usan Gemini en Gmail para redactar correos electrónicos de campañas, boletines y alcance al cliente. Sin embargo, los caracteres invisibles del texto copiado pueden parecer poco profesionales cuando se pegan en plantillas de correo electrónico HTML. Eliminar estos caracteres antes de finalizar garantiza una comunicación por correo electrónico limpia y profesional.'
                : locale === 'fr'
                ? 'Les équipes marketing utilisent Gemini dans Gmail pour rédiger des e-mails de campagne, des newsletters et de la prospection client. Cependant, les caractères invisibles du texte copié peuvent paraître peu professionnels une fois collés dans des modèles HTML. Supprimer ces caractères avant finalisation garantit une communication e-mail propre et professionnelle.'
                : locale === 'ja'
                ? 'マーケティングチームはGmailでGeminiを使用してキャンペーンメール、ニュースレター、顧客アウトリーチを作成します。ただし、コピーしたテキストに含まれる見えない文字は、HTMLメールテンプレートに貼り付けるとプロフェッショナルでなく見えることがあります。最終化前にこれらの文字を削除することで、クリーンでプロフェッショナルなメールコミュニケーションが保証されます。'
                : locale === 'id'
                ? 'Tim pemasaran menggunakan Gemini di Gmail untuk menyusun email kampanye, newsletter, dan penjangkauan pelanggan. Namun, karakter tak terlihat pada teks yang disalin dapat terlihat tidak profesional saat ditempel ke template email HTML. Menghapus karakter ini sebelum finalisasi memastikan komunikasi email yang bersih dan profesional.'
                : 'Marketing teams use Gemini in Gmail to draft campaign emails, newsletters, and customer outreach. However, invisible characters in copied text can appear unprofessional when pasted into HTML email templates. Removing these characters before finalizing ensures clean, professional email communication.'}
            </p>
            <div className="space-y-2">
              <div className="flex items-start gap-2 text-sm text-neutral-600 dark:text-neutral-400">
                <CheckCircle size={16} className="text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                <span>
                  {locale === 'de'
                    ? 'Sauberer HTML-E-Mail-Vorlagen-Export'
                    : locale === 'pt-BR'
                    ? 'Exportação limpa de modelos de e-mail HTML'
                    : locale === 'ms-MY'
                    ? 'Eksport templat e-mel HTML yang bersih'
                    : locale === 'es'
                    ? 'Exportación limpia de plantillas de correo electrónico HTML'
                    : locale === 'fr'
                    ? 'Export propre de modèles e-mail HTML'
                    : locale === 'ja'
                    ? 'クリーンなHTMLメールテンプレートエクスポート'
                    : locale === 'id'
                    ? 'Ekspor template email HTML yang bersih'
                    : 'Clean HTML email template export'}
                </span>
              </div>
            </div>
          </div>

          {/* Use Case 2: SEO Content in Google Docs */}
          <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950/30 dark:to-emerald-950/30 p-8 rounded-2xl border-2 border-green-200 dark:border-green-800 hover:shadow-2xl transition-[box-shadow,transform] duration-300 hover:-translate-y-1">
            <div className="w-14 h-14 bg-gradient-to-br from-green-600 to-emerald-700 rounded-xl flex items-center justify-center mb-5 shadow-lg">
              <TrendingUp className="text-white" size={28} />
            </div>
            <h3 className="text-xl font-bold text-neutral-900 dark:text-white mb-3">
              {locale === 'de'
                ? 'SEO-Inhaltserstellung'
                : locale === 'pt-BR'
                ? 'Criação de Conteúdo SEO'
                : locale === 'ms-MY'
                ? 'Penciptaan Kandungan SEO'
                : locale === 'es'
                ? 'Creación de Contenido SEO'
                : locale === 'fr'
                ? 'Création de Contenu SEO'
                : locale === 'ja'
                ? 'SEOコンテンツ作成'
                : locale === 'id'
                ? 'Pembuatan Konten SEO'
                : 'SEO Content Creation'}
            </h3>
            <p className="text-neutral-700 dark:text-neutral-300 mb-4 leading-relaxed">
              {locale === 'de'
                ? 'SEO-Spezialisten verwenden Gemini in Google Docs, um Blogbeiträge, Artikelgliederungen und Meta-Beschreibungen zu erstellen. Unsichtbare Zeichen im kopierten Text können beim Einfügen in ein CMS Formatierungsprobleme verursachen. Das Entfernen dieser Zeichen vor der Veröffentlichung sorgt für sauberen, gut formatierten Text im CMS.'
                : locale === 'pt-BR'
                ? 'Especialistas em SEO usam o Gemini no Google Docs para criar posts de blog, esboços de artigos e meta descrições. Caracteres invisíveis no texto copiado podem causar problemas de formatação ao colar em um CMS. Remover esses caracteres antes de publicar garante um texto limpo e bem formatado no CMS.'
                : locale === 'ms-MY'
                ? 'Pakar SEO menggunakan Gemini dalam Google Docs untuk mencipta catatan blog, rangka artikel, dan penerangan meta. Aksara tidak kelihatan dalam teks yang disalin boleh menyebabkan masalah pemformatan apabila ditampal ke dalam CMS. Membuang aksara ini sebelum menerbitkan memastikan teks yang bersih dan diformat dengan baik dalam CMS.'
                : locale === 'es'
                ? 'Los especialistas en SEO usan Gemini en Google Docs para crear publicaciones de blog, esquemas de artículos y metadescripciones. Los caracteres invisibles del texto copiado pueden causar problemas de formato al pegarlo en un CMS. Eliminar estos caracteres antes de publicar garantiza un texto limpio y bien formateado en el CMS.'
                : locale === 'fr'
                ? 'Les spécialistes SEO utilisent Gemini dans Google Docs pour créer des articles de blog, des plans d\'articles et des méta-descriptions. Les caractères invisibles du texte copié peuvent causer des problèmes de formatage lors du collage dans un CMS. Supprimer ces caractères avant publication garantit un texte propre et bien formaté dans le CMS.'
                : locale === 'ja'
                ? 'SEOスペシャリストはGoogle DocsでGeminiを使用してブログ記事、記事の概要、メタディスクリプションを作成します。コピーしたテキストに含まれる見えない文字は、CMSに貼り付ける際にフォーマット問題を引き起こす可能性があります。公開前にこれらの文字を削除することで、CMS内でクリーンで整ったテキストになります。'
                : locale === 'id'
                ? 'Spesialis SEO menggunakan Gemini di Google Docs untuk membuat postingan blog, outline artikel, dan meta deskripsi. Karakter tak terlihat pada teks yang disalin dapat menyebabkan masalah format saat ditempel ke CMS. Menghapus karakter ini sebelum publikasi memastikan teks yang bersih dan terformat dengan baik di CMS.'
                : 'SEO specialists use Gemini in Google Docs to create blog posts, article outlines, and meta descriptions. Invisible characters in copied text can cause formatting issues when pasted into a CMS. Removing these characters before publishing ensures clean, well-formatted text in the CMS.'}
            </p>
            <div className="space-y-2">
              <div className="flex items-start gap-2 text-sm text-neutral-600 dark:text-neutral-400">
                <CheckCircle size={16} className="text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                <span>
                  {locale === 'de'
                    ? 'Saubere Einfügung in Ihr CMS'
                    : locale === 'pt-BR'
                    ? 'Colagem limpa no seu CMS'
                    : locale === 'ms-MY'
                    ? 'Tampalan bersih ke dalam CMS anda'
                    : locale === 'es'
                    ? 'Pegado limpio en tu CMS'
                    : locale === 'fr'
                    ? 'Collage propre dans votre CMS'
                    : locale === 'ja'
                    ? 'CMSへのクリーンな貼り付け'
                    : locale === 'id'
                    ? 'Tempel yang bersih ke CMS Anda'
                    : 'Clean paste into your CMS'}
                </span>
              </div>
            </div>
          </div>

          {/* Use Case 3: Data Analysis in Google Sheets */}
          <div className="bg-gradient-to-br from-purple-50 to-fuchsia-50 dark:from-purple-950/30 dark:to-fuchsia-950/30 p-8 rounded-2xl border-2 border-purple-200 dark:border-purple-800 hover:shadow-2xl transition-[box-shadow,transform] duration-300 hover:-translate-y-1">
            <div className="w-14 h-14 bg-gradient-to-br from-purple-600 to-fuchsia-700 rounded-xl flex items-center justify-center mb-5 shadow-lg">
              <BarChart3 className="text-white" size={28} />
            </div>
            <h3 className="text-xl font-bold text-neutral-900 dark:text-white mb-3">
              {locale === 'de'
                    ? 'Datenanalyse & Berichterstattung'
                    : locale === 'pt-BR'
                    ? 'Análise de Dados e Relatórios'
                    : locale === 'ms-MY'
                    ? 'Analisis Data & Pelaporan'
                    : locale === 'es'
                    ? 'Análisis de Datos e Informes'
                    : locale === 'fr'
                    ? 'Analyse de Données et Rapports'
                    : locale === 'ja'
                    ? 'データ分析とレポート'
                    : locale === 'id'
                    ? 'Analisis Data & Pelaporan'
                    : 'Data Analysis & Reporting'}
            </h3>
            <p className="text-neutral-700 dark:text-neutral-300 mb-4 leading-relaxed">
              {locale === 'de'
                    ? 'Datenanalysten verwenden Gemini in Google Sheets, um Formeln zu generieren, Datenerkenntnisse zu interpretieren und Analysezusammenfassungen zu schreiben. Wenn Gemini-generierte Zellen in Business-Intelligence-Tools, SQL-Datenbanken oder Kundenberichte exportiert werden, können unsichtbare Zeichen Datenbankabfragen unterbrechen, CSV-Importe beschädigen und die Datenintegrität beeinträchtigen.'
                    : locale === 'pt-BR'
                    ? 'Analistas de dados usam o Gemini no Google Sheets para gerar fórmulas, interpretar insights de dados e escrever resumos de análise. Quando células geradas pelo Gemini são exportadas para ferramentas de business intelligence, bancos de dados SQL ou relatórios de clientes, caracteres invisíveis podem quebrar consultas de banco de dados, corromper importações CSV e afetar a integridade dos dados.'
                    : locale === 'ms-MY'
                    ? 'Penganalisis data menggunakan Gemini dalam Google Sheets untuk menjana formula, mentafsir cerapan data, dan menulis ringkasan analisis. Apabila sel yang dijana Gemini dieksport ke alat kecerdasan perniagaan, pangkalan data SQL, atau laporan klien, aksara tidak kelihatan boleh memecahkan pertanyaan pangkalan data, merosakkan import CSV, dan menjejaskan integriti data.'
                    : locale === 'es'
                    ? 'Los analistas de datos usan Gemini en Google Sheets para generar fórmulas, interpretar información de datos y escribir resúmenes de análisis. Cuando las celdas generadas por Gemini se exportan a herramientas de inteligencia empresarial, bases de datos SQL o informes de clientes, los caracteres invisibles pueden romper consultas de bases de datos, corromper importaciones CSV y afectar la integridad de los datos.'
                    : locale === 'fr'
                    ? 'Les analystes de données utilisent Gemini dans Google Sheets pour générer des formules, interpréter des insights de données et rédiger des résumés d\'analyse. Lors de l\'exportation vers des outils de BI, bases de données SQL ou rapports clients, les caractères invisibles peuvent casser les requêtes, corrompre les imports CSV et affecter l\'intégrité des données.'
                    : locale === 'ja'
                    ? 'データアナリストはGoogle SheetsでGeminiを使用して数式を生成し、データの洞察を解釈し、分析サマリーを作成します。Gemini生成セルをBIツール、SQLデータベース、クライアントレポートにエクスポートすると、見えない文字がデータベースクエリを壊し、CSVインポートを破損し、データの整合性に影響を与える可能性があります。'
                    : locale === 'id'
                    ? 'Analis data menggunakan Gemini di Google Sheets untuk menghasilkan formula, menginterpretasi wawasan data, dan menulis ringkasan analisis. Saat sel yang dihasilkan Gemini diekspor ke alat BI, database SQL, atau laporan klien, karakter tak terlihat dapat merusak kueri database, merusak impor CSV, dan mempengaruhi integritas data.'
                    : 'Data analysts use Gemini in Google Sheets to generate formulas, interpret data insights, and write analysis summaries. When Gemini-generated cells are exported to business intelligence tools, SQL databases, or client reports, invisible characters can break database queries, corrupt CSV imports, and affect data integrity.'}
            </p>
            <div className="space-y-2">
              <div className="flex items-start gap-2 text-sm text-neutral-600 dark:text-neutral-400">
                <CheckCircle size={16} className="text-purple-600 dark:text-purple-400 mt-0.5 flex-shrink-0" />
                <span>
                  {locale === 'de'
                    ? 'Saubere CSV/Excel-Exporte'
                    : locale === 'pt-BR'
                    ? 'Exportações CSV/Excel limpas'
                    : locale === 'ms-MY'
                    ? 'Eksport CSV/Excel yang bersih'
                    : locale === 'es'
                    ? 'Exportaciones CSV/Excel limpias'
                    : locale === 'fr'
                    ? 'Exports CSV/Excel propres'
                    : locale === 'ja'
                    ? 'クリーンなCSV/Excelエクスポート'
                    : locale === 'id'
                    ? 'Ekspor CSV/Excel yang bersih'
                    : 'Clean CSV/Excel exports'}
                </span>
              </div>
              <div className="flex items-start gap-2 text-sm text-neutral-600 dark:text-neutral-400">
                <CheckCircle size={16} className="text-purple-600 dark:text-purple-400 mt-0.5 flex-shrink-0" />
                <span>
                  {locale === 'de'
                    ? 'SQL-Abfrage-Kompatibilität'
                    : locale === 'pt-BR'
                    ? 'Compatibilidade de consultas SQL'
                    : locale === 'ms-MY'
                    ? 'Keserasian pertanyaan SQL'
                    : locale === 'es'
                    ? 'Compatibilidad de consultas SQL'
                    : locale === 'fr'
                    ? 'Compatibilité des requêtes SQL'
                    : locale === 'ja'
                    ? 'SQLクエリの互換性'
                    : locale === 'id'
                    ? 'Kompatibilitas kueri SQL'
                    : 'SQL query compatibility'}
                </span>
              </div>
              <div className="flex items-start gap-2 text-sm text-neutral-600 dark:text-neutral-400">
                <CheckCircle size={16} className="text-purple-600 dark:text-purple-400 mt-0.5 flex-shrink-0" />
                <span>
                  {locale === 'de'
                    ? 'Verhindert Datenbankfehler'
                    : locale === 'pt-BR'
                    ? 'Previne erros de banco de dados'
                    : locale === 'ms-MY'
                    ? 'Mencegah ralat pangkalan data'
                    : locale === 'es'
                    ? 'Prevenir errores de base de datos'
                    : locale === 'fr'
                    ? 'Prévient les erreurs de base de données'
                    : locale === 'ja'
                    ? 'データベースエラーを防止'
                    : locale === 'id'
                    ? 'Mencegah kesalahan database'
                    : 'Prevents database errors'}
                </span>
              </div>
            </div>
          </div>

          {/* Use Case 4: Copywriting & Creative Content */}
          <div className="bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-950/30 dark:to-amber-950/30 p-8 rounded-2xl border-2 border-orange-200 dark:border-orange-800 hover:shadow-2xl transition-[box-shadow,transform] duration-300 hover:-translate-y-1">
            <div className="w-14 h-14 bg-gradient-to-br from-orange-600 to-amber-700 rounded-xl flex items-center justify-center mb-5 shadow-lg">
              <PenTool className="text-white" size={28} />
            </div>
            <h3 className="text-xl font-bold text-neutral-900 dark:text-white mb-3">
              {locale === 'de'
                    ? 'Kreatives Copywriting'
                    : locale === 'pt-BR'
                    ? 'Copywriting Criativo'
                    : locale === 'ms-MY'
                    ? 'Copywriting Kreatif'
                    : locale === 'es'
                    ? 'Copywriting Creativo'
                    : locale === 'fr'
                    ? 'Rédaction Publicitaire Créative'
                    : locale === 'ja'
                    ? 'クリエイティブコピーライティング'
                    : locale === 'id'
                    ? 'Copywriting Kreatif'
                    : 'Creative Copywriting'}
            </h3>
            <p className="text-neutral-700 dark:text-neutral-300 mb-4 leading-relaxed">
              {locale === 'de'
                    ? 'Content-Ersteller und Werbetexter verwenden Gemini, um Social-Media-Posts, Anzeigentexte und Marketingmaterialien zu erstellen. Wenn dieser Inhalt in Content-Management-Systeme (WordPress, Webflow, Squarespace) oder Social-Media-Planungstools (Buffer, Hootsuite) kopiert wird, können unsichtbare Zeichen Formatierungsprobleme verursachen.'
                    : locale === 'pt-BR'
                    ? 'Criadores de conteúdo e copywriters usam o Gemini para redigir posts em mídias sociais, cópias de anúncios e materiais de marketing. Quando este conteúdo é copiado para sistemas de gerenciamento de conteúdo (WordPress, Webflow, Squarespace) ou ferramentas de agendamento de mídias sociais (Buffer, Hootsuite), caracteres invisíveis podem causar problemas de formatação.'
                    : locale === 'ms-MY'
                    ? 'Pencipta kandungan dan penulis salinan menggunakan Gemini untuk merangka catatan media sosial, salinan iklan, dan bahan pemasaran. Apabila kandungan ini disalin ke dalam sistem pengurusan kandungan (WordPress, Webflow, Squarespace) atau alat penjadualan media sosial (Buffer, Hootsuite), aksara tidak kelihatan boleh menyebabkan masalah pemformatan.'
                    : locale === 'es'
                    ? 'Los creadores de contenido y redactores publicitarios usan Gemini para redactar publicaciones en redes sociales, textos publicitarios y materiales de marketing. Cuando este contenido se copia en sistemas de gestión de contenido (WordPress, Webflow, Squarespace) o herramientas de programación de redes sociales (Buffer, Hootsuite), los caracteres invisibles pueden causar problemas de formato.'
                    : locale === 'fr'
                    ? 'Les créateurs de contenu et rédacteurs publicitaires utilisent Gemini pour rédiger des publications sur les réseaux sociaux, des textes publicitaires et du matériel marketing. Lors de la copie vers des CMS (WordPress, Webflow, Squarespace) ou des outils de planification sociale (Buffer, Hootsuite), les caractères invisibles peuvent causer des problèmes de formatage.'
                    : locale === 'ja'
                    ? 'コンテンツクリエイターやコピーライターはGeminiを使用してソーシャルメディア投稿、広告コピー、マーケティング素材を作成します。このコンテンツをCMS（WordPress、Webflow、Squarespace）やSNS管理ツール（Buffer、Hootsuite）にコピーすると、見えない文字がフォーマット問題を引き起こすことがあります。'
                    : locale === 'id'
                    ? 'Kreator konten dan copywriter menggunakan Gemini untuk membuat postingan media sosial, teks iklan, dan materi pemasaran. Saat konten ini disalin ke CMS (WordPress, Webflow, Squarespace) atau alat penjadwalan media sosial (Buffer, Hootsuite), karakter tak terlihat dapat menyebabkan masalah format.'
                    : 'Content creators and copywriters use Gemini to draft social media posts, ad copy, and marketing materials. When this content is copied into content management systems (WordPress, Webflow, Squarespace) or social media scheduling tools (Buffer, Hootsuite), invisible characters can cause formatting issues.'}
            </p>
            <div className="space-y-2">
              <div className="flex items-start gap-2 text-sm text-neutral-600 dark:text-neutral-400">
                <CheckCircle size={16} className="text-orange-600 dark:text-orange-400 mt-0.5 flex-shrink-0" />
                <span>
                  {locale === 'de'
                    ? 'CMS-Formatierung beibehalten'
                    : locale === 'pt-BR'
                    ? 'Preserve formatação do CMS'
                    : locale === 'ms-MY'
                    ? 'Kekalkan pemformatan CMS'
                    : locale === 'es'
                    ? 'Preservar formato CMS'
                    : locale === 'fr'
                    ? 'Préserver le formatage CMS'
                    : locale === 'ja'
                    ? 'CMSフォーマットを維持'
                    : locale === 'id'
                    ? 'Menjaga format CMS'
                    : 'Preserve CMS formatting'}
                </span>
              </div>
              <div className="flex items-start gap-2 text-sm text-neutral-600 dark:text-neutral-400">
                <CheckCircle size={16} className="text-orange-600 dark:text-orange-400 mt-0.5 flex-shrink-0" />
                <span>
                  {locale === 'de'
                    ? 'Social-Media-Planung funktioniert'
                    : locale === 'pt-BR'
                    ? 'Agendamento de mídia social funciona'
                    : locale === 'ms-MY'
                    ? 'Penjadualan media sosial berfungsi'
                    : locale === 'es'
                    ? 'La programación de redes sociales funciona'
                    : locale === 'fr'
                    ? 'La planification sociale fonctionne'
                    : locale === 'ja'
                    ? 'SNS予約投稿が機能'
                    : locale === 'id'
                    ? 'Penjadwalan media sosial berfungsi'
                    : 'Social media scheduling works'}
                </span>
              </div>
            </div>
          </div>

          {/* Use Case 5: Business Documentation */}
          <div className="bg-gradient-to-br from-cyan-50 to-sky-50 dark:from-cyan-950/30 dark:to-sky-950/30 p-8 rounded-2xl border-2 border-cyan-200 dark:border-cyan-800 hover:shadow-2xl transition-[box-shadow,transform] duration-300 hover:-translate-y-1">
            <div className="w-14 h-14 bg-gradient-to-br from-cyan-600 to-sky-700 rounded-xl flex items-center justify-center mb-5 shadow-lg">
              <Briefcase className="text-white" size={28} />
            </div>
            <h3 className="text-xl font-bold text-neutral-900 dark:text-white mb-3">
              {locale === 'de'
                    ? 'Geschäftsdokumentation'
                    : locale === 'pt-BR'
                    ? 'Documentação Empresarial'
                    : locale === 'ms-MY'
                    ? 'Dokumentasi Perniagaan'
                    : locale === 'es'
                    ? 'Documentación Empresarial'
                    : locale === 'fr'
                    ? 'Documentation Professionnelle'
                    : locale === 'ja'
                    ? 'ビジネス文書作成'
                    : locale === 'id'
                    ? 'Dokumentasi Bisnis'
                    : 'Business Documentation'}
            </h3>
            <p className="text-neutral-700 dark:text-neutral-300 mb-4 leading-relaxed">
              {locale === 'de'
                    ? 'Unternehmensteams nutzen Gemini, um Geschäftsvorschläge, Besprechungszusammenfassungen, Projektpläne und interne Dokumentation zu erstellen. Wenn diese Dokumente als PDF exportiert, mit Kunden geteilt oder in Projekt-Management-Tools (Notion, Confluence, Jira) importiert werden, können unsichtbare Zeichen im kopierten Text Durchsuchbarkeit, Indexierung und professionelle Präsentation beeinträchtigen.'
                    : locale === 'pt-BR'
                    ? 'Equipes empresariais usam o Gemini para criar propostas de negócios, resumos de reuniões, planos de projeto e documentação interna. Quando esses documentos são exportados como PDFs, compartilhados com clientes ou importados para ferramentas de gerenciamento de projetos (Notion, Confluence, Jira), caracteres invisíveis no texto copiado podem afetar a pesquisabilidade, indexação e apresentação profissional.'
                    : locale === 'ms-MY'
                    ? 'Pasukan perusahaan menggunakan Gemini untuk mencipta cadangan perniagaan, ringkasan mesyuarat, rancangan projek, dan dokumentasi dalaman. Apabila dokumen ini dieksport sebagai PDF, dikongsi dengan klien, atau diimport ke dalam alat pengurusan projek (Notion, Confluence, Jira), aksara tidak kelihatan dalam teks yang disalin boleh menjejaskan kebolehcarian, pengindeksan, dan persembahan profesional.'
                    : locale === 'es'
                    ? 'Los equipos empresariales usan Gemini para crear propuestas de negocios, resúmenes de reuniones, planes de proyectos y documentación interna. Cuando estos documentos se exportan como PDFs, se comparten con clientes o se importan a herramientas de gestión de proyectos (Notion, Confluence, Jira), los caracteres invisibles del texto copiado pueden afectar la capacidad de búsqueda, indexación y presentación profesional.'
                    : locale === 'fr'
                    ? 'Les équipes professionnelles utilisent Gemini pour créer des propositions commerciales, des comptes rendus de réunion, des plans de projet et de la documentation interne. Lors de l\'exportation en PDF, du partage avec les clients ou de l\'import dans des outils de gestion de projet (Notion, Confluence, Jira), les caractères invisibles du texte copié peuvent affecter la recherche, l\'indexation et la présentation professionnelle.'
                    : locale === 'ja'
                    ? '企業チームはGeminiを使用してビジネス提案書、会議要約、プロジェクト計画、社内文書を作成します。これらの文書をPDFとしてエクスポートしたり、クライアントと共有したり、プロジェクト管理ツール（Notion、Confluence、Jira）にインポートすると、コピーしたテキストに含まれる見えない文字が検索性、インデックス化、プロフェッショナルな表示に影響を与える可能性があります。'
                    : locale === 'id'
                    ? 'Tim perusahaan menggunakan Gemini untuk membuat proposal bisnis, ringkasan rapat, rencana proyek, dan dokumentasi internal. Saat dokumen ini diekspor sebagai PDF, dibagikan dengan klien, atau diimpor ke alat manajemen proyek (Notion, Confluence, Jira), karakter tak terlihat pada teks yang disalin dapat mempengaruhi pencarian, pengindeksan, dan presentasi profesional.'
                    : 'Enterprise teams use Gemini to create business proposals, meeting summaries, project plans, and internal documentation. When these documents are exported as PDFs, shared with clients, or imported into project management tools (Notion, Confluence, Jira), invisible characters in copied text can affect searchability, indexing, and professional presentation.'}
            </p>
            <div className="space-y-2">
              <div className="flex items-start gap-2 text-sm text-neutral-600 dark:text-neutral-400">
                <CheckCircle size={16} className="text-cyan-600 dark:text-cyan-400 mt-0.5 flex-shrink-0" />
                <span>
                  {locale === 'de'
                    ? 'Professionelle PDF-Exporte'
                    : locale === 'pt-BR'
                    ? 'Exportações PDF profissionais'
                    : locale === 'ms-MY'
                    ? 'Eksport PDF profesional'
                    : locale === 'es'
                    ? 'Exportaciones PDF profesionales'
                    : locale === 'fr'
                    ? 'Exports PDF professionnels'
                    : locale === 'ja'
                    ? 'プロフェッショナルなPDFエクスポート'
                    : locale === 'id'
                    ? 'Ekspor PDF profesional'
                    : 'Professional PDF exports'}
                </span>
              </div>
              <div className="flex items-start gap-2 text-sm text-neutral-600 dark:text-neutral-400">
                <CheckCircle size={16} className="text-cyan-600 dark:text-cyan-400 mt-0.5 flex-shrink-0" />
                <span>
                  {locale === 'de'
                    ? 'Saubere Dokumentensuche'
                    : locale === 'pt-BR'
                    ? 'Busca de documentos limpa'
                    : locale === 'ms-MY'
                    ? 'Carian dokumen yang bersih'
                    : locale === 'es'
                    ? 'Búsqueda de documentos limpia'
                    : locale === 'fr'
                    ? 'Recherche de documents propre'
                    : locale === 'ja'
                    ? 'クリーンな文書検索'
                    : locale === 'id'
                    ? 'Pencarian dokumen yang bersih'
                    : 'Clean document search'}
                </span>
              </div>
              <div className="flex items-start gap-2 text-sm text-neutral-600 dark:text-neutral-400">
                <CheckCircle size={16} className="text-cyan-600 dark:text-cyan-400 mt-0.5 flex-shrink-0" />
                <span>
                  {locale === 'de'
                    ? 'Notion/Confluence-Integration'
                    : locale === 'pt-BR'
                    ? 'Integração Notion/Confluence'
                    : locale === 'ms-MY'
                    ? 'Integrasi Notion/Confluence'
                    : locale === 'es'
                    ? 'Integración Notion/Confluence'
                    : locale === 'fr'
                    ? 'Intégration Notion/Confluence'
                    : locale === 'ja'
                    ? 'Notion/Confluence統合'
                    : locale === 'id'
                    ? 'Integrasi Notion/Confluence'
                    : 'Notion/Confluence integration'}
                </span>
              </div>
            </div>
          </div>

          {/* Use Case 6: Multilingual Content */}
          <div className="bg-gradient-to-br from-rose-50 to-pink-50 dark:from-rose-950/30 dark:to-pink-950/30 p-8 rounded-2xl border-2 border-rose-200 dark:border-rose-800 hover:shadow-2xl transition-[box-shadow,transform] duration-300 hover:-translate-y-1">
            <div className="w-14 h-14 bg-gradient-to-br from-rose-600 to-pink-700 rounded-xl flex items-center justify-center mb-5 shadow-lg">
              <Globe className="text-white" size={28} />
            </div>
            <h3 className="text-xl font-bold text-neutral-900 dark:text-white mb-3">
              {locale === 'de'
                    ? 'Mehrsprachige Übersetzung'
                    : locale === 'pt-BR'
                    ? 'Tradução Multilíngue'
                    : locale === 'ms-MY'
                    ? 'Terjemahan Pelbagai Bahasa'
                    : locale === 'es'
                    ? 'Traducción Multilingüe'
                    : locale === 'fr'
                    ? 'Traduction Multilingue'
                    : locale === 'ja'
                    ? '多言語翻訳'
                    : locale === 'id'
                    ? 'Terjemahan Multibahasa'
                    : 'Multilingual Translation'}
            </h3>
            <p className="text-neutral-700 dark:text-neutral-300 mb-4 leading-relaxed">
              {locale === 'de'
                ? 'Globale Teams verwenden Geminis mehrsprachige Fähigkeiten, um Inhalte über Sprachen hinweg zu übersetzen. Wenn übersetzte Inhalte in professionelle Übersetzungsmanagementsysteme (Smartling, Lokalise, Phrase) exportiert oder in internationalen Marketingkampagnen verwendet werden, können unsichtbare Zeichen zu Kodierungsproblemen führen, Übersetzungsspeicher beschädigen und Unicode-Kompatibilität beeinträchtigen.'
                : locale === 'pt-BR'
                ? 'Equipes globais usam as capacidades multilíngues do Gemini para traduzir conteúdo entre idiomas. Quando o conteúdo traduzido é exportado para sistemas profissionais de gerenciamento de tradução (Smartling, Lokalise, Phrase) ou usado em campanhas de marketing internacional, caracteres invisíveis podem causar problemas de codificação, corromper memória de tradução e afetar compatibilidade Unicode.'
                : locale === 'ms-MY'
                ? 'Pasukan global menggunakan keupayaan pelbagai bahasa Gemini untuk menterjemah kandungan merentasi bahasa. Apabila kandungan terjemahan dieksport ke sistem pengurusan terjemahan profesional (Smartling, Lokalise, Phrase) atau digunakan dalam kempen pemasaran antarabangsa, aksara tidak kelihatan boleh menyebabkan masalah pengekodan, merosakkan memori terjemahan, dan menjejaskan keserasian Unicode.'
                : locale === 'es'
                ? 'Los equipos globales usan las capacidades multilingües de Gemini para traducir contenido entre idiomas. Cuando el contenido traducido se exporta a sistemas profesionales de gestión de traducción (Smartling, Lokalise, Phrase) o se usa en campañas de marketing internacional, los caracteres invisibles pueden causar problemas de codificación, corromper la memoria de traducción y afectar la compatibilidad Unicode.'
                : locale === 'fr'
                ? 'Les équipes mondiales utilisent les capacités multilingues de Gemini pour traduire du contenu entre les langues. Lors de l\'exportation vers des systèmes professionnels de gestion de traduction (Smartling, Lokalise, Phrase) ou pour des campagnes marketing internationales, les caractères invisibles peuvent causer des problèmes d\'encodage, corrompre la mémoire de traduction et affecter la compatibilité Unicode.'
                : locale === 'ja'
                ? 'グローバルチームはGeminiの多言語機能を使用して言語間でコンテンツを翻訳します。翻訳されたコンテンツをプロフェッショナルな翻訳管理システム（Smartling、Lokalise、Phrase）にエクスポートしたり、国際マーケティングキャンペーンで使用すると、見えない文字がエンコーディング問題を引き起こし、翻訳メモリを破損し、Unicode互換性に影響を与える可能性があります。'
                : locale === 'id'
                ? 'Tim global menggunakan kemampuan multibahasa Gemini untuk menerjemahkan konten antar bahasa. Saat konten terjemahan diekspor ke sistem manajemen terjemahan profesional (Smartling, Lokalise, Phrase) atau digunakan dalam kampanye pemasaran internasional, karakter tak terlihat dapat menyebabkan masalah encoding, merusak memori terjemahan, dan mempengaruhi kompatibilitas Unicode.'
                : 'Global teams use Gemini\'s multilingual capabilities to translate content across languages. When translated content is exported to professional translation management systems (Smartling, Lokalise, Phrase) or used in international marketing campaigns, invisible characters can cause encoding issues, corrupt translation memory, and affect Unicode compatibility.'}
            </p>
            <div className="space-y-2">
              <div className="flex items-start gap-2 text-sm text-neutral-600 dark:text-neutral-400">
                <CheckCircle size={16} className="text-rose-600 dark:text-rose-400 mt-0.5 flex-shrink-0" />
                <span>
                  {locale === 'de'
                    ? 'Unicode-Kodierungsintegrität'
                    : locale === 'pt-BR'
                    ? 'Integridade de codificação Unicode'
                    : locale === 'ms-MY'
                    ? 'Integriti pengekodan Unicode'
                    : locale === 'es'
                    ? 'Integridad de codificación Unicode'
                    : locale === 'fr'
                    ? 'Intégrité de l\'encodage Unicode'
                    : locale === 'ja'
                    ? 'Unicodeエンコーディングの整合性'
                    : locale === 'id'
                    ? 'Integritas encoding Unicode'
                    : 'Unicode encoding integrity'}
                </span>
              </div>
              <div className="flex items-start gap-2 text-sm text-neutral-600 dark:text-neutral-400">
                <CheckCircle size={16} className="text-rose-600 dark:text-rose-400 mt-0.5 flex-shrink-0" />
                <span>
                  {locale === 'de'
                    ? 'TMS-Kompatibilität'
                    : locale === 'pt-BR'
                    ? 'Compatibilidade com TMS'
                    : locale === 'ms-MY'
                    ? 'Keserasian TMS'
                    : locale === 'es'
                    ? 'Compatibilidad con TMS'
                    : locale === 'fr'
                    ? 'Compatibilité TMS'
                    : locale === 'ja'
                    ? 'TMS互換性'
                    : locale === 'id'
                    ? 'Kompatibilitas TMS'
                    : 'TMS compatibility'}
                </span>
              </div>
              <div className="flex items-start gap-2 text-sm text-neutral-600 dark:text-neutral-400">
                <CheckCircle size={16} className="text-rose-600 dark:text-rose-400 mt-0.5 flex-shrink-0" />
                <span>
                  {locale === 'de'
                    ? 'Saubere mehrsprachige Kampagnen'
                    : locale === 'pt-BR'
                    ? 'Campanhas multilíngues limpas'
                    : locale === 'ms-MY'
                    ? 'Kempen pelbagai bahasa yang bersih'
                    : locale === 'es'
                    ? 'Campañas multilingües limpias'
                    : locale === 'fr'
                    ? 'Campagnes multilingues propres'
                    : locale === 'ja'
                    ? 'クリーンな多言語キャンペーン'
                    : locale === 'id'
                    ? 'Kampanye multibahasa yang bersih'
                    : 'Clean multilingual campaigns'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Call-to-Action for Use Cases */}
        <div className="mt-12 bg-gradient-to-r from-blue-600 to-indigo-700 rounded-2xl p-8 text-center text-white shadow-2xl">
          <h3 className="text-2xl font-bold mb-3">
            {locale === 'de'
                    ? 'Bereit, Ihre Gemini-Workflows zu bereinigen?'
                    : locale === 'pt-BR'
                    ? 'Pronto para Limpar seus Fluxos de Trabalho do Gemini?'
                    : locale === 'ms-MY'
                    ? 'Bersedia untuk Membersihkan Aliran Kerja Gemini Anda?'
                    : locale === 'es'
                    ? '¿Listo para Limpiar tus Flujos de Trabajo de Gemini?'
                    : locale === 'fr'
                    ? 'Prêt à Nettoyer vos Flux de Travail Gemini ?'
                    : locale === 'ja'
                    ? 'Geminiワークフローをクリーンにする準備はできましたか？'
                    : locale === 'id'
                    ? 'Siap Membersihkan Alur Kerja Gemini Anda?'
                    : 'Ready to Clean Your Gemini Workflows?'}
          </h3>
          <p className="text-blue-100 mb-6 max-w-2xl mx-auto">
            {locale === 'de'
                    ? 'Nutzen Sie unser Tool zur Entfernung unsichtbarer Zeichen, um Ihre Gemini-generierten Inhalte produktionsreif zu machen.'
                    : locale === 'pt-BR'
                    ? 'Use nossa ferramenta de remoção de caracteres invisíveis para tornar seu conteúdo gerado pelo Gemini pronto para produção.'
                    : locale === 'ms-MY'
                    ? 'Gunakan alat pembuangan aksara tidak kelihatan kami untuk menjadikan kandungan yang dijana Gemini anda bersedia untuk pengeluaran.'
                    : locale === 'es'
                    ? 'Usa nuestra herramienta de eliminación de caracteres invisibles para que tu contenido generado por Gemini esté listo para producción.'
                    : locale === 'fr'
                    ? 'Utilisez notre outil de suppression des caractères invisibles pour rendre votre contenu généré par Gemini prêt pour la production.'
                    : locale === 'ja'
                    ? '当社の見えない文字削除ツールを使って、Gemini生成コンテンツを本番環境対応にしましょう。'
                    : locale === 'id'
                    ? 'Gunakan alat penghapus karakter tak terlihat kami untuk membuat konten yang dihasilkan Gemini siap produksi.'
                    : 'Use our invisible character remover to make your Gemini-generated content production-ready.'}
          </p>
          <a
            href="#remover"
            className="inline-flex items-center gap-2 bg-white text-blue-700 px-8 py-4 rounded-xl font-semibold hover:bg-blue-50 transition-[background-color,box-shadow,transform] duration-300 shadow-lg hover:shadow-xl hover:scale-105"
          >
            {locale === 'de'
              ? 'Jetzt unsichtbare Zeichen entfernen'
              : locale === 'pt-BR'
              ? 'Remover Caracteres Invisíveis Agora'
              : locale === 'ms-MY'
              ? 'Buang Aksara Tidak Kelihatan Sekarang'
              : locale === 'es'
              ? 'Eliminar Caracteres Invisibles Ahora'
              : locale === 'fr'
              ? 'Supprimer les Caractères Invisibles Maintenant'
              : locale === 'ja'
              ? '今すぐ見えない文字を削除'
              : locale === 'id'
              ? 'Hapus Karakter Tak Terlihat Sekarang'
              : 'Remove Invisible Characters Now'}
            <ArrowRight size={20} />
          </a>
        </div>
      </section>

      {/* Comparison - directly imported (no longer lazy) */}
      <Comparison />

      {/* Internal linking banner - Gemini Image Watermark Remover */}
      <GeminiImageBanner />

      {/* Related Tools Section - Internal Linking for SEO */}
      <section className="mt-16">
        <div className="bg-white dark:bg-charcoal-900 rounded-2xl shadow-lg dark:shadow-dark-lg border border-neutral-200 dark:border-charcoal-700/50 p-8">
          <h2 className="text-2xl font-bold text-neutral-900 dark:text-white mb-6">
            {locale === 'de'
                    ? 'Wasserzeichen von anderen KI-Tools entfernen'
                    : locale === 'pt-BR'
                    ? 'Remover Marcas d\'Água de Outras Ferramentas de IA'
                    : locale === 'ms-MY'
                    ? 'Buang Tanda Air daripada Alat AI Lain'
                    : locale === 'es'
                    ? 'Eliminar Marcas de Agua de Otras Herramientas de IA'
                    : locale === 'fr'
                    ? 'Supprimer les Filigranes d\'Autres Outils IA'
                    : locale === 'ja'
                    ? '他のAIツールから透かしを削除'
                    : locale === 'id'
                    ? 'Hapus Watermark dari Alat AI Lain'
                    : 'Remove Watermarks from Other AI Tools'}
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            <Link
              to="/remove-chatgpt-watermarks"
              className="p-6 border border-neutral-200 dark:border-charcoal-700 rounded-xl hover:shadow-lg transition-[box-shadow,border-color] hover:border-blue-400 dark:hover:border-blue-600 group"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg group-hover:scale-110 transition-transform">
                  <Sparkles size={20} className="text-blue-600 dark:text-blue-400" />
                </div>
                <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">
                  ChatGPT
                </h3>
              </div>
              <p className="text-neutral-600 dark:text-neutral-400 text-sm">
                {locale === 'de'
                    ? 'Entfernen Sie unsichtbare Zeichen aus ChatGPT-Ausgaben'
                    : locale === 'pt-BR'
                    ? 'Remova caracteres invisíveis de saídas do ChatGPT'
                    : locale === 'ms-MY'
                    ? 'Buang aksara tidak kelihatan daripada output ChatGPT'
                    : locale === 'es'
                    ? 'Elimina caracteres invisibles de salidas de ChatGPT'
                    : locale === 'fr'
                    ? 'Supprimez les caractères invisibles des sorties ChatGPT'
                    : locale === 'ja'
                    ? 'ChatGPTの出力から見えない文字を削除'
                    : locale === 'id'
                    ? 'Hapus karakter tak terlihat dari output ChatGPT'
                    : 'Remove invisible characters from ChatGPT output'}
              </p>
            </Link>

            <Link
              to="/remove-claude-watermarks"
              className="p-6 border border-neutral-200 dark:border-charcoal-700 rounded-xl hover:shadow-lg transition-[box-shadow,border-color] hover:border-purple-400 dark:hover:border-purple-600 group"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg group-hover:scale-110 transition-transform">
                  <Shield size={20} className="text-purple-600 dark:text-purple-400" />
                </div>
                <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">
                  Claude AI
                </h3>
              </div>
              <p className="text-neutral-600 dark:text-neutral-400 text-sm">
                {locale === 'de'
                    ? 'Funktioniert mit Ausgaben aus jedem Claude-Modell'
                    : locale === 'pt-BR'
                    ? 'Funciona com saídas de qualquer modelo Claude'
                    : locale === 'ms-MY'
                    ? 'Berfungsi dengan output daripada mana-mana model Claude'
                    : locale === 'es'
                    ? 'Funciona con salidas de cualquier modelo Claude'
                    : locale === 'fr'
                    ? 'Fonctionne avec les sorties de tout modèle Claude'
                    : locale === 'ja'
                    ? 'あらゆるClaudeモデルの出力に対応'
                    : locale === 'id'
                    ? 'Bekerja dengan output dari model Claude apa pun'
                    : 'Works with output from any Claude model'}
              </p>
            </Link>

            <div className="p-6 border border-neutral-200 dark:border-charcoal-700 rounded-xl opacity-60">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-neutral-100 dark:bg-neutral-800 rounded-lg">
                  <Zap size={20} className="text-neutral-600 dark:text-neutral-400" />
                </div>
                <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">
                  More Coming
                </h3>
              </div>
              <p className="text-neutral-600 dark:text-neutral-400 text-sm">
                {locale === 'de'
                    ? 'Weitere KI-Wasserzeichen-Entferner in Kürze verfügbar'
                    : locale === 'pt-BR'
                    ? 'Mais removedores de marcas d\'água de IA em breve'
                    : locale === 'ms-MY'
                    ? 'Lebih banyak penyingkir tanda air AI akan datang'
                    : locale === 'es'
                    ? 'Más eliminadores de marcas de agua de IA próximamente'
                    : locale === 'fr'
                    ? 'D\'autres suppresseurs de filigranes IA bientôt disponibles'
                    : locale === 'ja'
                    ? '他のAI透かし削除ツールも近日公開予定'
                    : locale === 'id'
                    ? 'Lebih banyak penghapus watermark AI segera hadir'
                    : 'More AI watermark removers coming soon'}
              </p>
            </div>
          </div>
        </div>
      </section>


      {/* Related Content Section */}
      <div className="mt-16">
        <RelatedContentForPSEO
          pageId="gemini-watermark"
          pageTitle={locale === 'de'
            ? 'Google Gemini Wasserzeichen entfernen'
            : locale === 'pt-BR'
            ? 'Remover Marcas d\'Água do Google Gemini'
            : locale === 'ms-MY'
            ? 'Buang Tanda Air Google Gemini'
            : locale === 'es'
            ? 'Eliminar Marcas de Agua de Google Gemini'
            : locale === 'fr'
            ? 'Supprimer les Filigranes Google Gemini'
            : locale === 'ja'
            ? 'Google Gemini透かしを削除'
            : locale === 'id'
            ? 'Hapus Watermark Google Gemini'
            : 'Remove Google Gemini Watermarks'}
          pageDescription={locale === 'de'
            ? 'Entfernen Sie unsichtbare Unicode-Zeichen aus Text, den Sie aus Google Gemini (ehemals Bard) kopiert haben. Unser Tool erkennt und entfernt Zeichen ohne Breite und andere unsichtbare Formatierungsartefakte aus jeder Gemini-Ausgabe. Es hat keinen Einfluss auf SynthID, Googles tatsächliches KI-Wasserzeichen, das in der Wortwahl eingebettet ist und nicht durch das Entfernen von Zeichen beseitigt werden kann.'
            : locale === 'pt-BR'
            ? 'Remova caracteres Unicode invisíveis de texto copiado do Google Gemini (anteriormente Bard). Nossa ferramenta detecta e remove caracteres de largura zero e outros artefatos de formatação invisíveis de qualquer saída do Gemini. Isso não afeta o SynthID, a marca d\'água de IA real do Google, que está incorporada na escolha de palavras e não pode ser removida ao eliminar caracteres.'
            : locale === 'ms-MY'
            ? 'Buang aksara Unicode yang tidak kelihatan daripada teks yang anda tampal daripada Google Gemini (dahulunya Bard). Alat kami mengesan dan membuang aksara lebar-sifar dan artifak pemformatan tidak kelihatan lain daripada sebarang output Gemini. Ia tidak menjejaskan SynthID, tanda air AI sebenar Google, yang terbenam dalam pilihan perkataan dan tidak boleh dibuang dengan membuang aksara.'
            : locale === 'es'
            ? 'Elimina caracteres Unicode invisibles del texto que copiaste de Google Gemini (anteriormente Bard). Nuestra herramienta detecta y elimina caracteres de ancho cero y otros artefactos de formato invisibles de cualquier salida de Gemini. No afecta a SynthID, la marca de agua de IA real de Google, que está incrustada en la elección de palabras y no se puede eliminar borrando caracteres.'
            : locale === 'fr'
            ? 'Supprimez les caractères Unicode invisibles du texte que vous avez copié depuis Google Gemini (anciennement Bard). Notre outil détecte et supprime les caractères de largeur nulle et autres artefacts de formatage invisibles de toute sortie Gemini. Cela n\'affecte pas SynthID, le véritable filigrane IA de Google, qui est intégré dans le choix des mots et ne peut pas être supprimé en effaçant des caractères.'
            : locale === 'ja'
            ? 'Google Gemini（旧Bard）からコピーしたテキストから不可視のUnicode文字を削除します。当社のツールは、Geminiの出力に含まれるゼロ幅文字やその他の不可視のフォーマットアーティファクトを検出して削除します。これは、GoogleのAI透かしであるSynthIDには影響しません。SynthIDは単語の選択に埋め込まれており、文字を削除しても取り除くことはできません。'
            : locale === 'id'
            ? 'Hapus karakter Unicode tak terlihat dari teks yang Anda salin dari Google Gemini (sebelumnya Bard). Alat kami mendeteksi dan menghapus karakter lebar nol dan artefak format tak terlihat lainnya dari output Gemini apa pun. Ini tidak mempengaruhi SynthID, watermark AI asli Google, yang tertanam dalam pilihan kata dan tidak dapat dihapus dengan menghapus karakter.'
            : 'Remove invisible Unicode characters from text you\'ve pasted out of Google Gemini (formerly Bard). Our tool detects and strips zero-width characters and other invisible formatting artifacts from any Gemini output. It does not affect SynthID, Google\'s actual AI watermark, which is embedded in word choice and cannot be removed by stripping characters.'}
          locale={locale}
        />
      </div>

      {/* Gemini-Specific FAQs */}
      <div className="mt-16">
        <div className="bg-gradient-to-br from-accent-50 via-neutral-100 to-purple-50 dark:bg-charcoal-900 rounded-2xl shadow-xl dark:shadow-dark-lg border border-neutral-200 dark:border-charcoal-700/50 overflow-hidden relative subtle-texture">
          <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-accent-400/10 to-purple-400/10 rounded-full blur-3xl opacity-0 dark:opacity-100"></div>
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-gradient-to-tr from-purple-400/10 to-accent-400/10 rounded-full blur-3xl opacity-0 dark:opacity-100"></div>

          <div className="relative p-8 md:p-12">
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-2 bg-accent-200/50 dark:bg-white/10 backdrop-blur-sm px-4 py-2 rounded-lg mb-4 border border-accent-300 dark:border-white/15 shadow-sm">
                <MessageCircle className="w-5 h-5 text-accent-700 dark:text-white" />
                <span className="font-semibold text-sm text-accent-800 dark:text-white">
                  {locale === 'de'
                    ? 'Häufig gestellte Fragen'
                    : locale === 'pt-BR'
                    ? 'Perguntas Frequentes'
                    : locale === 'ms-MY'
                    ? 'Soalan Lazim'
                    : locale === 'es'
                    ? 'Preguntas Frecuentes'
                    : locale === 'fr'
                    ? 'Questions Fréquentes'
                    : locale === 'ja'
                    ? 'よくある質問'
                    : locale === 'id'
                    ? 'Pertanyaan yang Sering Diajukan'
                    : 'Frequently Asked Questions'}
                </span>
              </div>

              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4 leading-tight text-neutral-900 dark:text-white">
                {locale === 'de'
                    ? 'Google Gemini Wasserzeichen FAQ'
                    : locale === 'pt-BR'
                    ? 'FAQ de Marcas d\'Água do Google Gemini'
                    : locale === 'ms-MY'
                    ? 'Soalan Lazim Tanda Air Google Gemini'
                    : locale === 'es'
                    ? 'Preguntas Frecuentes sobre Marcas de Agua de Google Gemini'
                    : locale === 'fr'
                    ? 'FAQ sur les Filigranes Google Gemini'
                    : locale === 'ja'
                    ? 'Google Gemini透かしに関するFAQ'
                    : locale === 'id'
                    ? 'FAQ Watermark Google Gemini'
                    : 'Google Gemini Watermark FAQ'}
              </h2>

              <p className="text-base md:text-lg text-neutral-700 dark:text-neutral-200 mb-8 max-w-3xl mx-auto leading-relaxed">
                {locale === 'de'
                    ? 'Finden Sie Antworten auf häufige Fragen zum Entfernen unsichtbarer Zeichen aus Gemini-Text.'
                    : locale === 'pt-BR'
                    ? 'Encontre respostas para perguntas comuns sobre remover caracteres invisíveis do texto do Gemini.'
                    : locale === 'ms-MY'
                    ? 'Cari jawapan kepada soalan lazim tentang membuang aksara tidak kelihatan daripada teks Gemini.'
                    : locale === 'es'
                    ? 'Encuentra respuestas a preguntas comunes sobre cómo eliminar caracteres invisibles del texto de Gemini.'
                    : locale === 'fr'
                    ? 'Trouvez des réponses aux questions fréquentes sur la suppression des caractères invisibles du texte Gemini.'
                    : locale === 'ja'
                    ? 'Geminiのテキストから見えない文字を削除することに関するよくある質問への回答をご覧ください。'
                    : locale === 'id'
                    ? 'Temukan jawaban untuk pertanyaan umum tentang menghapus karakter tak terlihat dari teks Gemini.'
                    : 'Find answers to common questions about removing invisible characters from Gemini text.'}
              </p>
            </div>

            {/* INP FIX: FAQ accordion extracted to own component so clicks only re-render
                the accordion (~120 lines) instead of this entire 2000+ line page */}
            <GeminiFAQAccordion />
          </div>
        </div>
      </div>

      {/* Related Tools */}
      <Suspense fallback={null}>
        <RelatedTools currentPage="gemini" />
      </Suspense>

      {/* Related Articles */}
      <Suspense fallback={null}>
        <RelatedArticles topic="gemini" />
      </Suspense>

      {/* Feedback CTA - directly imported (no longer lazy) */}
      <FeedbackCTA />
      </div>
    </div>
  );
}
